import { Box, Button, Card, CircularProgress, LinearProgress, Skeleton, Typography } from "@mui/material";
import { BubbleMenu, useEditor } from "@tiptap/react";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AddPaymentModal } from "src/components/dashboard/addPaymenModal";
import { ReadOnlyTakeOverModal } from "src/components/dashboard/articleEditor/ReadOnlyTakeOverModal";
import { RegenerateDropdown } from "src/components/dashboard/brief/rightSection/aiArticle/regenerateDropdown";
import ImageGenerationModal from "src/components/dashboard/editor/ImageGenerationModal";
import MenuBar from "src/components/dashboard/editor/MenuBar";
import { MagicWandIcon } from "src/icons/magic-wand-icon";
import { getActivePlan } from "src/slices/auth/thunks";

import Head from "next/head";
import {
  selectIsHumanized,
  setIsHumanized,
  setShowHumanizeContent,
} from "src/slices/humanize";
import { endHumanization } from "src/slices/humanize/thunks";
import { applyFactCheckFinding, saveArticleEEATScore, takeOverArticle } from "src/slices/articles/thunks";
import { calculateEEATScore, getEEATScore } from "src/slices/eeat/thunks";
import { aiWordsUpgradeModalShowed, snackAdded } from "src/slices/system";
import { selectCurrentPlan } from "src/slices/system/selectors";
import { useDispatch, useSelector } from "src/store";
import { copyHtmlToClipboard, insertUnlockCardWithTipTap } from "src/utils/clipboardUtils";
import { Export2Docx } from "src/utils/docs";
import { fetchProjectImageSettings, signedLambdaFetch } from "src/utils/imageGeneration";
import { signedHumanizeFetch } from "src/utils/humanizeStream";
import {
  addMetaToArticle,
  countSelectedWords,
  extractSelectedBlocks,
  getEditorExtensions,
  getMarkdownConverter,
  getWordCount,
  SelectedTextItem,
  setupTurndownService,
  useDualDebounce,
} from "src/utils/editorUtils";
import Editor from "../editor";
import { FactCheckDecorationRange } from "../editor/extensions/FactCheckDecorations";
import { Onboarding } from "../onboarding";
import { OptimizeContentDialog } from "../optimizeContent/optimizeContentDialog";
import { AutoReview, FactCheckFinding } from "./factCheckSidebar";
import Sidebar from "./sidebar";
import { useArticlePresenceContext } from "./ArticlePresenceContext";

// Props for the ArticleEditorComponent
interface ArticleEditorComponentProps {
  id: string;
  title?: string;
  description?: string;
  isOptimizedMode?: boolean;
  hideArticle?: boolean;
  fetchContent: (id: string) => Promise<any>;
  saveContent: (content: string, options?: any) => Promise<any>;
  isDownloadContent?: boolean;
  isCopyContent?: boolean;
  additionalProps?: any;
  isEEATMode?: boolean;
}

const buildNormalizedTextMap = (input: string) => {
  let normalized = "";
  const normalizedToRaw: number[] = [];
  let previousWasSpace = true;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    const isSpace = /\s/.test(char);

    if (isSpace) {
      if (!previousWasSpace) {
        normalized += " ";
        normalizedToRaw.push(i);
        previousWasSpace = true;
      }
      continue;
    }

    normalized += char.toLowerCase();
    normalizedToRaw.push(i);
    previousWasSpace = false;
  }

  if (normalized.endsWith(" ")) {
    normalized = normalized.slice(0, -1);
    normalizedToRaw.pop();
  }

  return { normalized, normalizedToRaw };
};

const unwrapDynamoValue = (value: any): any => {
  if (value == null || typeof value !== "object") return value;
  if ("S" in value) return value.S;
  if ("N" in value) return Number(value.N);
  if ("BOOL" in value) return Boolean(value.BOOL);
  if ("NULL" in value) return null;
  if ("M" in value) {
    const out: Record<string, any> = {};
    Object.entries(value.M || {}).forEach(([k, v]) => {
      out[k] = unwrapDynamoValue(v);
    });
    return out;
  }
  if ("L" in value) {
    return (value.L || []).map((item: any) => unwrapDynamoValue(item));
  }
  return value;
};

/** Normalize unicode characters that commonly differ between AI output and
 *  rich-text editors: smart quotes, em/en dashes. All replacements are
 *  1-to-1 character length to preserve offset mapping when applied to both
 *  needle and haystack. */
const normalizeUnicode = (s: string): string =>
  s
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")   // smart single quotes → '
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')   // smart double quotes → "
    .replace(/[\u2013\u2014]/g, "-");               // en-dash / em-dash → -

/** Convert markdown table-like text to plain visible text used by TipTap.
 *  Example: "| A | B |" -> "A B" */
const normalizeMarkdownTableText = (input: string): string => {
  const text = normalizeUnicode(input || "").trim();
  if (!text.includes("|")) return text;

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const normalizedLines = lines
    // Drop markdown separator lines like | --- | :---: |
    .filter((line) => !/^[\|\-\:\s]+$/.test(line))
    .map((line) => {
      const noEdgePipes = line.replace(/^\|/, "").replace(/\|$/, "");
      return noEdgePipes
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean)
        .join(" ");
    })
    .filter(Boolean);

  return normalizedLines.join(" ").replace(/\s+/g, " ").trim();
};

const buildTextSegments = (editor: any) => {
  const segments: Array<{ start: number; end: number; pos: number; text: string; isNewline?: boolean }> = [];
  let cursor = 0;
  let lastBlockEnd = -1;

  editor.state.doc.descendants((node: any, pos: number) => {
    // Insert newline separator between block-level nodes so cross-paragraph
    // text matching behaves correctly (matches markdown newline boundaries).
    if (node.isBlock && !node.isLeaf && segments.length > 0 && pos > lastBlockEnd) {
      segments.push({ start: cursor, end: cursor + 1, pos, text: "\n", isNewline: true });
      cursor += 1;
    }
    if (node.isBlock) {
      lastBlockEnd = pos + node.nodeSize;
    }
    if (!node.isText || !node.text) return;
    const text = String(node.text);
    const start = cursor;
    const end = start + text.length;
    segments.push({ start, end, pos, text });
    cursor = end;
  });
  return { segments, combinedText: segments.map((s) => s.text).join("") };
};

/** Walk the editor doc and collect the generation "opportunity" metadata of
 *  every image node that still carries it, keyed by the image src URL. This is
 *  the source of truth persisted alongside the article so the per-image
 *  "regenerate" control survives reloads. Deriving from the doc means a
 *  regenerated image's src swap is handled for free (the stale old-src entry is
 *  simply absent from the freshly-walked map). */
const buildImageOpportunityMap = (editor: any): Record<string, any> => {
  const map: Record<string, any> = {};
  if (!editor) return map;
  editor.state.doc.descendants((node: any) => {
    if (node.type.name === "image" && node.attrs?.src && node.attrs?.imageOpportunity) {
      map[node.attrs.src] = node.attrs.imageOpportunity;
    }
  });
  return map;
};

const charIndexToPos = (
  segments: Array<{ start: number; end: number; pos: number; isNewline?: boolean }>,
  index: number,
  bias: "start" | "end" = "start"
) => {
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    // Use exclusive upper bound so boundary indexes (text.end === newline.start)
    // fall through to the next segment. The last segment uses inclusive end
    // to handle the document-end position.
    const atEnd = i === segments.length - 1;
    if (index >= segment.start && (atEnd ? index <= segment.end : index < segment.end)) {
      if (segment.isNewline) {
        // Virtual newline — snap to adjacent real text to avoid landing on a
        // paragraph boundary (which would merge paragraphs on replace).
        // Bias-aware: "start" snaps forward, "end" snaps backward.
        if (bias === "start") {
          const next = segments[i + 1];
          if (next && !next.isNewline) return next.pos;
          const prev = segments[i - 1];
          if (prev && !prev.isNewline) return prev.pos + (prev.end - prev.start);
        } else {
          const prev = segments[i - 1];
          if (prev && !prev.isNewline) return prev.pos + (prev.end - prev.start);
          const next = segments[i + 1];
          if (next && !next.isNewline) return next.pos;
        }
        return null;
      }
      const offset = Math.max(0, Math.min(index - segment.start, segment.end - segment.start));
      return segment.pos + offset;
    }
  }
  return null;
};

/** Check whether two strings are close enough to be considered the same text
 *  after minor whitespace / punctuation drift. */
const isCloseMatch = (a: string, b: string): boolean => {
  const na = a.toLowerCase().replace(/\s+/g, " ").trim();
  const nb = b.toLowerCase().replace(/\s+/g, " ").trim();
  if (na === nb) return true;
  if (na.length > 0 && nb.length > 0) {
    const shorter = Math.min(na.length, nb.length);
    const longer = Math.max(na.length, nb.length);
    // Allow up to 20% length difference and check prefix containment
    if (shorter / longer > 0.8 && na.includes(nb.slice(0, shorter))) return true;
  }
  return false;
};


type PrebuiltSegments = { segments: Array<{ start: number; end: number; pos: number; text: string; isNewline?: boolean }>; combinedText: string };

// Opt-in debug logging for the article load/save/lock paths. Off by default so the
// `[article-content]` probes (briefId, versions, content lengths) never ship to prod
// consoles; set NEXT_PUBLIC_DEBUG_ARTICLE="true" locally to re-enable while debugging.
const DEBUG_ARTICLE = process.env.NEXT_PUBLIC_DEBUG_ARTICLE === "true";
const debugArticle = (...args: any[]) => {
  if (DEBUG_ARTICLE) console.log(...args);
};
const debugArticleWarn = (...args: any[]) => {
  if (DEBUG_ARTICLE) console.warn(...args);
};

const getArticleApiBaseUrl = () => {
  if (
    process.env.NEXT_PUBLIC_BRANCH === "dev" ||
    process.env.NODE_ENV === "development"
  ) {
    return "https://py-staging.seowind.io";
  }
  if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
    return "https://py-staging.seowind.io";
  }
  return "https://py.seowind.io";
};

/** Score how well surrounding text matches the locator context.
 *  0 = no context or no match, 1 = partial match, 2 = full match per side. Max 4. */
const scoreContextMatch = (
  combinedText: string,
  matchStart: number,
  matchEnd: number,
  locator?: FactCheckFinding["locator"]
): number => {
  if (!locator) return 0;
  let score = 0;

  if (locator.context_before) {
    const ctx = locator.context_before.trim().toLowerCase();
    const textBefore = combinedText.slice(Math.max(0, matchStart - ctx.length - 20), matchStart).toLowerCase();
    if (textBefore.includes(ctx)) score += 2;
    else if (ctx.length > 10 && textBefore.includes(ctx.slice(-10))) score += 1;
  }

  if (locator.context_after) {
    const ctx = locator.context_after.trim().toLowerCase();
    const textAfter = combinedText.slice(matchEnd, matchEnd + ctx.length + 20).toLowerCase();
    if (textAfter.includes(ctx)) score += 2;
    else if (ctx.length > 10 && textAfter.includes(ctx.slice(0, 10))) score += 1;
  }

  return score;
};

const findAllOccurrences = (haystack: string, needle: string): number[] => {
  const indices: number[] = [];
  const lowerHaystack = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let pos = 0;
  while (pos < lowerHaystack.length) {
    const idx = lowerHaystack.indexOf(lowerNeedle, pos);
    if (idx < 0) break;
    indices.push(idx);
    pos = idx + 1;
  }
  return indices;
};

/** From a list of candidate char-index ranges, pick the best one using:
 *  1. Context score (highest wins)
 *  2. Tie-breaker: closest to locator start_char (if present)
 *  3. Confidence threshold: if best score is 0 and multiple candidates exist, return null */
const pickBestCandidate = (
  candidates: Array<{ charStart: number; charEnd: number }>,
  combinedText: string,
  segments: PrebuiltSegments["segments"],
  finding: FactCheckFinding
): { from: number; to: number } | null => {
  if (candidates.length === 0) return null;

  // Single candidate — use directly (no ambiguity)
  if (candidates.length === 1) {
    const c = candidates[0];
    const from = charIndexToPos(segments, c.charStart, "start");
    const to = charIndexToPos(segments, c.charEnd, "end");
    return (from && to && from < to) ? { from, to } : null;
  }

  // Multiple candidates — score and rank
  const scored = candidates.map(c => {
    const from = charIndexToPos(segments, c.charStart, "start");
    const to = charIndexToPos(segments, c.charEnd, "end");
    const score = scoreContextMatch(combinedText, c.charStart, c.charEnd, finding.locator);
    return { from, to, score, charStart: c.charStart };
  }).filter(c => c.from && c.to && c.from < c.to);

  if (scored.length === 0) return null;

  // Sort: highest score first, then closest to locator start_char as tie-breaker
  const locatorStart = finding.locator?.start_char != null ? Number(finding.locator.start_char) : null;
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (locatorStart != null && Number.isFinite(locatorStart)) {
      return Math.abs(a.charStart - locatorStart) - Math.abs(b.charStart - locatorStart);
    }
    return 0;
  });

  const best = scored[0];

  // When there is no locator but section_heading is present (agent-proposed
  // findings), use the heading position to pick the occurrence that belongs
  // to that section rather than always falling back to the first one.
  if (best.score === 0 && scored.length > 1 && !finding.locator && finding.section_heading) {
    const headingText = finding.section_heading.trim().replace(/^#+\s*/, "").toLowerCase();
    const headingIdx = combinedText.toLowerCase().indexOf(headingText);
    if (headingIdx >= 0) {
      const afterHeading = scored.filter(c => (c.charStart ?? 0) >= headingIdx);
      const pick = afterHeading[0] ?? scored[0];
      return { from: pick.from!, to: pick.to! };
    }
  }

  // Confidence threshold: if best score is 0 and there are multiple candidates,
  // we can't confidently pick one — return null to avoid risky apply.
  // Exception: if finding has no locator at all, context scoring is impossible
  // so we fall back to first occurrence rather than showing nothing.
  if (best.score === 0 && scored.length > 1 && finding.locator) {
    return null;
  }

  return { from: best.from!, to: best.to! };
};

const findRangeForFinding = (
  editor: any,
  finding: FactCheckFinding,
  prebuiltSegments?: PrebuiltSegments,
  // Once any apply/remove has happened in this session, backend-supplied
  // start_char/end_char on every other finding are off by the doc-length delta.
  // The text-validation guard catches most of those, but coincidental matches
  // at a stale offset can land in the wrong place. Caller passes true to skip
  // Strategy 1 entirely after the first apply.
  skipLocatorOffsets?: boolean
): FactCheckDecorationRange | null => {
  const rawNeedle = normalizeUnicode((finding.original_text || "").trim());
  const tableNeedle = normalizeMarkdownTableText(rawNeedle);
  // When original_text is a full markdown link [label](url), the editor renders
  // only the label text — strip link/image syntax so the highlight can land on
  // the visible text.
  const visibleNeedle = normalizeUnicode(rawNeedle.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1").trim());
  const needleVariants = Array.from(new Set([rawNeedle, tableNeedle, visibleNeedle].filter(Boolean)));
  if (needleVariants.length === 0) return null;

  let segments: PrebuiltSegments["segments"];
  let combinedText: string;
  if (prebuiltSegments) {
    // Caller already built segments and normalized the text
    segments = prebuiltSegments.segments;
    combinedText = prebuiltSegments.combinedText;
  } else {
    const built = buildTextSegments(editor);
    if (!built.combinedText) return null;
    segments = built.segments;
    combinedText = normalizeUnicode(built.combinedText);
  }

  // ── Strategy 1: Locator offsets ──────────────────────────────────────
  // Backend computes start_char/end_char against plain text (same space as
  // ProseMirror text nodes). Validate the text at the offset before trusting it.
  const locator = finding.locator;
  // Coerce to numbers — DynamoDB / JSON may return strings like "1804"
  const rawStartChar = locator?.start_char != null ? Number(locator.start_char) : NaN;
  const rawEndChar = locator?.end_char != null ? Number(locator.end_char) : NaN;
  if (
    !skipLocatorOffsets &&
    Number.isFinite(rawStartChar) &&
    Number.isFinite(rawEndChar) &&
    rawStartChar < rawEndChar
  ) {
    const startChar = rawStartChar;
    const endChar = rawEndChar;
    const textAtOffset = combinedText.slice(startChar, endChar);
    if (textAtOffset && needleVariants.some((variant) => isCloseMatch(textAtOffset, variant))) {
      const from = charIndexToPos(segments, startChar, "start");
      const to = charIndexToPos(segments, endChar, "end");
      if (from && to && from < to) {
        return { from, to, severity: finding.severity, findingId: finding.id };
      }
    }
  }

  // ── Strategy 2: Exact text match ─────────────────────────────────────
  for (const needle of needleVariants) {
    const occurrences = findAllOccurrences(combinedText, needle);
    if (occurrences.length === 0) continue;
    const result = pickBestCandidate(
      occurrences.map(idx => ({ charStart: idx, charEnd: idx + needle.length })),
      combinedText, segments, finding
    );
    if (result) return { ...result, severity: finding.severity, findingId: finding.id };
  }

  // Normalized whitespace fallback — find all occurrences, pick best by context
  for (const needle of needleVariants) {
    const normalizedHaystack = buildNormalizedTextMap(combinedText);
    const normalizedNeedle = buildNormalizedTextMap(needle).normalized;
    if (!normalizedNeedle) continue;

    const normCandidates: Array<{ charStart: number; charEnd: number }> = [];
    let searchPos = 0;
    while (searchPos < normalizedHaystack.normalized.length) {
      const normalizedIndex = normalizedHaystack.normalized.indexOf(normalizedNeedle, searchPos);
      if (normalizedIndex < 0) break;
      const rawStart = normalizedHaystack.normalizedToRaw[normalizedIndex];
      const rawEndMapIndex = normalizedIndex + normalizedNeedle.length - 1;
      const rawEndCharIndex = normalizedHaystack.normalizedToRaw[rawEndMapIndex];
      if (Number.isFinite(rawStart) && Number.isFinite(rawEndCharIndex)) {
        normCandidates.push({ charStart: rawStart, charEnd: rawEndCharIndex + 1 });
      }
      searchPos = normalizedIndex + 1;
    }

    if (normCandidates.length > 0) {
      const result = pickBestCandidate(normCandidates, combinedText, segments, finding);
      if (result) return { ...result, severity: finding.severity, findingId: finding.id };
    }
  }

  // ── Strategy 2.5: Head-tail anchor matching ─────────────────────────
  // When Claude slightly misquotes text (missing/extra words), split the
  // needle into head and tail portions and find the spanning region.
  for (const needle of needleVariants) {
    const words = needle.split(/\s+/);
    if (words.length >= 3) {
      const midpoint = Math.ceil(words.length / 2);
      const headPhrase = words.slice(0, midpoint).join(" ");
      const tailPhrase = words.slice(-Math.max(1, Math.floor(words.length / 3))).join(" ");

      // Find all head occurrences, then match corresponding tails
      const headOccurrences = findAllOccurrences(combinedText, headPhrase);
      const htCandidates: Array<{ charStart: number; charEnd: number }> = [];
      for (const headIdx of headOccurrences) {
        const searchAfter = headIdx + headPhrase.length;
        const tailWindow = combinedText.slice(searchAfter, searchAfter + needle.length * 2);
        const tailIdx = tailWindow.toLowerCase().indexOf(tailPhrase.toLowerCase());
        if (tailIdx >= 0) {
          htCandidates.push({ charStart: headIdx, charEnd: searchAfter + tailIdx + tailPhrase.length });
        }
      }

      if (htCandidates.length > 0) {
        const result = pickBestCandidate(htCandidates, combinedText, segments, finding);
        if (result) return { ...result, severity: finding.severity, findingId: finding.id };
      }
    }
  }

  // ── Strategy 3: Context-anchored search ──────────────────────────────
  // Use context_before to locate the approximate region, then search for
  // the needle nearby. Collect all candidates across chunks, then pick best.
  if (locator?.context_before) {
    const ctxBefore = locator.context_before.trim();
    const chunks = [ctxBefore];
    // Extract last clause after a delimiter (handles markdown list → block rendering mismatch)
    const lastDelim = Math.max(ctxBefore.lastIndexOf(","), ctxBefore.lastIndexOf("."), ctxBefore.lastIndexOf(";"));
    if (lastDelim > 0 && ctxBefore.length - lastDelim > 5) {
      chunks.push(ctxBefore.slice(lastDelim + 1).trim());
    }
    if (ctxBefore.length > 20) {
      chunks.push(ctxBefore.slice(-20).trim());
    }

    const ctxCandidates: Array<{ charStart: number; charEnd: number }> = [];
    const seenRanges = new Set<string>();

    for (const chunk of chunks) {
      if (chunk.length < 5) continue;
      // Find all context_before occurrences, not just the first
      const ctxOccurrences = findAllOccurrences(combinedText, chunk);
      for (const ctxIndex of ctxOccurrences) {
        const searchStart = ctxIndex + chunk.length;
        const maxNeedleLength = Math.max(...needleVariants.map((n) => n.length));
        const searchWindow = combinedText.slice(searchStart, searchStart + maxNeedleLength * 2 + 100);

        // Try exact needle match in window for each variant
        for (const needle of needleVariants) {
          const needleInWindow = searchWindow.toLowerCase().indexOf(needle.toLowerCase());
          if (needleInWindow >= 0) {
            const absoluteStart = searchStart + needleInWindow;
            const key = `${absoluteStart}:${absoluteStart + needle.length}`;
            if (!seenRanges.has(key)) {
              seenRanges.add(key);
              ctxCandidates.push({ charStart: absoluteStart, charEnd: absoluteStart + needle.length });
            }
          }
        }

        // Try head-tail anchor within the context window for each needle variant
        for (const needle of needleVariants) {
          const words = needle.split(/\s+/);
          if (words.length >= 3) {
            const midpoint = Math.ceil(words.length / 2);
            const headPhrase = words.slice(0, midpoint).join(" ");
            const tailPhrase = words.slice(-Math.max(1, Math.floor(words.length / 3))).join(" ");

            const headInWindow = searchWindow.toLowerCase().indexOf(headPhrase.toLowerCase());
            if (headInWindow >= 0) {
              const afterHead = headInWindow + headPhrase.length;
              const tailInWindow = searchWindow.slice(afterHead, afterHead + needle.length * 2).toLowerCase().indexOf(tailPhrase.toLowerCase());
              if (tailInWindow >= 0) {
                const absStart = searchStart + headInWindow;
                const absEnd = searchStart + afterHead + tailInWindow + tailPhrase.length;
                const key = `${absStart}:${absEnd}`;
                if (!seenRanges.has(key)) {
                  seenRanges.add(key);
                  ctxCandidates.push({ charStart: absStart, charEnd: absEnd });
                }
              }
            }
          }
        }
      }
    }

    if (ctxCandidates.length > 0) {
      const result = pickBestCandidate(ctxCandidates, combinedText, segments, finding);
      if (result) return { ...result, severity: finding.severity, findingId: finding.id };
    }
  }


  return null;
};

/** Resolve the editor range for an *applied* finding using its post-apply
 *  text + context, not the (preserved-as-audit) `original_text`. Reuses the
 *  same matcher with a synthetic finding shape; locator carries no offsets
 *  by design (offsets go stale instantly on any neighbouring edit). */
const findRangeForAppliedFinding = (
  editor: any,
  finding: FactCheckFinding,
  prebuiltSegments?: PrebuiltSegments
): FactCheckDecorationRange | null => {
  if (!finding.applied_text) return null;
  const synthetic: FactCheckFinding = {
    ...finding,
    original_text: finding.applied_text,
    locator: finding.applied_locator
      ? {
          context_before: finding.applied_locator.context_before,
          context_after: finding.applied_locator.context_after,
        }
      : undefined,
  };
  // Always skip Strategy 1 — applied_locator never carries offsets.
  return findRangeForFinding(editor, synthetic, prebuiltSegments, true);
};

const ArticleEditorComponent: React.FC<ArticleEditorComponentProps> = ({
  id,
  title = "Default Title",
  description = "Default Description",
  isOptimizedMode = false,
  hideArticle = false,
  fetchContent,
  saveContent,
  additionalProps = {},
  isEEATMode = false,
}) => {
  const dispatch = useDispatch();
  const presence = useArticlePresenceContext();
  // Live mirror of edit permission so async loops/handlers read the latest value.
  const canEditRef = React.useRef(true);
  canEditRef.current = presence.canEdit;
  // The holder sessionId for which the user dismissed the "article in use" modal via
  // "View read-only". A NEW holder (different sessionId) re-opens the prompt, which is
  // exactly the "demoted while away, then someone took over" case.
  const [dismissedHolderSessionId, setDismissedHolderSessionId] = useState<string | null>(null);
  const isHumanized = useSelector(selectIsHumanized);
  const currentPlan: any = useSelector(selectCurrentPlan);
  const isImageGenEnabled = currentPlan?.spreadsheet === "true" || currentPlan?.spreadsheet === true;
  const [showAIDropdown, setShowAIDropdown] = useState(false);
  const [selectedText, setSelectedText] = useState<SelectedTextItem[]>([]);
  const [shouldShowBubbleMenu, setShouldShowBubbleMenu] = useState(true);
  const [bottomMargin, setBottomMargin] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isWaitingForFirstChunk, setIsWaitingForFirstChunk] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isOptimizeContentDialogOpen, setIsOptimizeContentDialogOpen] =
    useState(false);
  const [cords, setCords] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  // Add state for selection coordinates to use in fixed positioning
  const [selectionCoords, setSelectionCoords] = useState({
    startCoords: { left: 0, right: 0, top: 0, bottom: 0 },
    endCoords: { left: 0, right: 0, top: 0, bottom: 0 },
  });
  const [html, setHtml] = useState("");
  const [humanizedHtml, setHumanizedHtml] = useState("");
  const htmlRef = useRef("");
  const humanizedHtmlRef = useRef("");
  const isContentLoaded = useRef(false);
  const [editorContent, setEditorContent] = useState<string>("");
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [isValidEvaluationData, setIsValidEvaluationData] =
    useState<boolean>(false);
  const [isGeneratingEEATScore, setIsGeneratingEEATScore] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [truncatedContent, setTruncatedContent] = useState("");
  const [isEditingHumanizedVersion, setIsEditingHumanizedVersion] =
    useState(false);
  const [isOptimizeContentDialogLoading, setIsOptimizeContentDialogLoading] =
    useState(false);
  const [articleLink, setArticleLink] = useState<string | null>(null);
  const [useScrollBasedTruncation, setUseScrollBasedTruncation] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isWritingMode, setIsWritingMode] = useState(false);
  const [factCheckFindings, setFactCheckFindings] = useState<FactCheckFinding[]>([]);
  // Mirror of factCheckFindings in a ref so async callbacks (e.g. the apply
  // handler) can read the latest list without waiting for a re-render. State
  // closures would otherwise capture a stale snapshot if findings changed
  // between user click and the callback firing.
  const factCheckFindingsRef = useRef<FactCheckFinding[]>([]);
  const [factCheckAutoReview, setFactCheckAutoReview] = useState<AutoReview | null>(null);
  const [activeFindingId, setActiveFindingId] = useState<number | null>(null);
  // True while a /article/apply-finding call is in flight. Drives the
  // sidebar's disabled state AND short-circuits decline (defense in depth).
  const [isApplyingFactCheck, setIsApplyingFactCheck] = useState(false);
  const isApplyingFactCheckRef = useRef(false);
  const [showImageGenModal, setShowImageGenModal] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [imageGenError, setImageGenError] = useState<string | null>(null);
  // Map of image-src -> generation "opportunity" metadata, loaded from the
  // saved article. Drives rehydration of the per-image "regenerate" control on
  // load (markdown can't carry the metadata, so it's persisted server-side).
  const [imageOpportunities, setImageOpportunities] = useState<Record<string, any>>({});
  const converter = getMarkdownConverter();
  const router = useRouter();

  // Refs to track programmatic changes and content saving
  const isContentSaving = React.useRef(false);
  const lastSavedContent = React.useRef("");
  // Serializes ALL content saves. Two /article/update in flight at once would read
  // the same versionRef baseline; the first advances the version and the second
  // then 409s (the "in the middle of edits, second change undone" self-conflict).
  // Chaining every save guarantees each reads versionRef AFTER the previous save
  // advanced it — no self-conflict, without adopting-and-retrying (which could
  // clobber a genuine other-writer).
  const saveChainRef = React.useRef<Promise<any>>(Promise.resolve());
  const isProgrammaticChange = React.useRef(false);
  // Optimistic-concurrency version: the version we last loaded/saved. Sent as
  // lastKnownVersion on every content write; a 409 VERSION_CONFLICT means someone
  // else advanced the article and we must reload rather than clobber.
  const versionRef = React.useRef<number>(0);

  // On a version conflict we reload HEAD (never clobber a newer version), but the
  // edit the user was trying to save would otherwise be silently lost. We buffer it
  // here and surface a "Restore my changes" banner so the user can put it back on
  // top of the freshly-loaded version and re-save — no data loss, no silent clobber.
  const [pendingConflictEdit, setPendingConflictEdit] = React.useState<{
    html: string;
    isHumanized: boolean;
  } | null>(null);

  // Create local ref to track which version we're currently editing (humanized or original)
  const isEditingHumanizedContent = React.useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sequence counter to prevent out-of-order toggle writes (Fix P1)
  const toggleSequence = React.useRef<number>(0);

  // Debounced save for fact-check finding statuses.
  // Writes are serialized so stale in-flight requests cannot overwrite newer state.
  const findingsSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest unsent args captured outside the timer's closure so flush can drain
  // them immediately if the debounce hasn't fired yet.
  const pendingFindingsArgs = React.useRef<{ findings: FactCheckFinding[]; content: string } | null>(null);
  // Promise ref (rather than a bare boolean) so flushPendingFindingsSave can
  // await the in-flight loop instead of polling.
  const findingsInFlightPromise = React.useRef<Promise<void> | null>(null);
  const decorationRunId = React.useRef(0);
  const lastPrebuiltRef = React.useRef<PrebuiltSegments | null>(null);
  // Once any apply/remove fires, every other finding's locator.start_char/end_char
  // is shifted by the doc-length delta. Skip Strategy 1 in the resolver after the
  // first apply, falling through to text + context matching.
  const localOffsetsDirtyRef = React.useRef(false);
  // Tracks the set of finding IDs we've seen so we can detect a fresh load
  // or a fresh fact-check pass. When the ID set changes, the new findings
  // came with offsets computed against current content — trust them again.
  // Apply/decline updates keep the same IDs, so they don't trigger a reset.
  const previousFindingIdsRef = React.useRef<string>("");
  const queuedFindingsPayload = React.useRef<{ findings: FactCheckFinding[]; content: string } | null>(null);
  const persistFactCheckFindings = useCallback(async (snapshot: FactCheckFinding[], contentSnapshot: string) => {
    queuedFindingsPayload.current = { findings: snapshot, content: contentSnapshot };
    if (findingsInFlightPromise.current) return findingsInFlightPromise.current;

    const promise = (async () => {
      try {
        while (queuedFindingsPayload.current) {
          const payload = queuedFindingsPayload.current;
          queuedFindingsPayload.current = null;

          try {
            // Metadata-only write: finding apply/decline status changes do NOT alter
            // article text (the real text edit goes through the version-guarded
            // /article/apply-finding). Sending `content` here would let a stale tab
            // clobber the body on an unguarded path — so we persist ONLY the findings.
            // We DO send lastKnownVersion so the backend rejects a pre-take-over
            // holder's delayed status save (stale review state) without bumping the
            // version; on conflict we reload HEAD to pick up the newer findings.
            const response = await fetch(`${getArticleApiBaseUrl()}/article/update`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                briefId: id,
                factCheckFindings: payload.findings,
                lastKnownVersion: versionRef.current,
              }),
            });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            const json = await response.json();
            if (json?.status_code === 409 || json?.data?.code === "VERSION_CONFLICT") {
              console.warn("[FactCheck] version conflict — reloading HEAD");
              handleVersionConflict();
              return;
            }
          } catch (err) {
            console.error("[FactCheck] Failed to save finding statuses:", err);
          }
        }
      } finally {
        findingsInFlightPromise.current = null;
      }
    })();
    findingsInFlightPromise.current = promise;
    return promise;
  }, [id]);
  const saveFactCheckFindings = useCallback((updatedFindings: FactCheckFinding[], contentSnapshot: string) => {
    // Stash args outside the timer's closure so flushPendingFindingsSave can
    // drain whatever was last requested even if the debounce hasn't fired.
    pendingFindingsArgs.current = { findings: updatedFindings, content: contentSnapshot };
    if (findingsSaveTimer.current) clearTimeout(findingsSaveTimer.current);
    findingsSaveTimer.current = setTimeout(() => {
      const args = pendingFindingsArgs.current;
      pendingFindingsArgs.current = null;
      findingsSaveTimer.current = null;
      if (args) persistFactCheckFindings(args.findings, args.content);
    }, 500);
  }, [persistFactCheckFindings]);

  // Drain any pending or in-flight fact-check status save before doing
  // something that depends on the article content (e.g. backend apply).
  // Without this, a queued decline/status save can land *after* the apply
  // and overwrite the applied content with pre-apply markdown.
  const flushPendingFindingsSave = useCallback(async () => {
    if (findingsSaveTimer.current) {
      clearTimeout(findingsSaveTimer.current);
      findingsSaveTimer.current = null;
    }
    const args = pendingFindingsArgs.current;
    pendingFindingsArgs.current = null;
    if (args) {
      // persistFactCheckFindings returns a promise that resolves when the
      // queue is fully drained (including any save kicked off just now).
      await persistFactCheckFindings(args.findings, args.content);
    } else if (findingsInFlightPromise.current) {
      await findingsInFlightPromise.current;
    }
  }, [persistFactCheckFindings]);

  // ── Image-opportunity persistence ───────────────────────────────────────
  // Generated images carry a generation "opportunity" (prompt, type, aspect
  // ratio, …) on the node that powers the per-image "regenerate" control. That
  // metadata is lost on the markdown save round-trip, so we persist a
  // src -> opportunity map alongside the content (mirrors factCheckFindings)
  // and rehydrate it onto the nodes on load.
  const lastSavedImageMapRef = React.useRef<string>("");
  // Mirror of imageOpportunities state so the rehydration logic can read the
  // latest map without depending on render-cycle closure timing.
  const imageOpportunitiesRef = React.useRef<Record<string, any>>({});
  const imageMapInFlightPromise = React.useRef<Promise<void> | null>(null);
  const queuedImageMapPayload = React.useRef<{ map: Record<string, any>; content: string } | null>(null);

  const persistImageOpportunities = useCallback(async (map: Record<string, any>, contentSnapshot: string) => {
    queuedImageMapPayload.current = { map, content: contentSnapshot };
    if (imageMapInFlightPromise.current) return imageMapInFlightPromise.current;

    const promise = (async () => {
      try {
        // Drain any debounced content save first so it lands and bumps the version
        // BEFORE this write reads versionRef — otherwise the two content writes would
        // self-conflict (both bump version, the later one 409s).
        try {
          await flushPendingSaves();
        } catch {
          /* flush errors surface via handleSaveContent's snack; proceed best-effort */
        }

        while (queuedImageMapPayload.current) {
          const payload = queuedImageMapPayload.current;
          queuedImageMapPayload.current = null;

          try {
            // This write changes content (new image src) + the map atomically, so it
            // IS version-guarded (sends lastKnownVersion). On a stale-version conflict
            // we reload HEAD rather than clobber a newer writer.
            const response = await fetch(`${getArticleApiBaseUrl()}/article/update`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                briefId: id,
                content: convertToMarkdown(payload.content),
                isHumanized: isEditingHumanizedContent.current ?? false,
                isEdited: false,
                imageOpportunities: payload.map,
                lastKnownVersion: versionRef.current,
              }),
            });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            const json = await response.json();
            if (json?.status_code === 409 || json?.data?.code === "VERSION_CONFLICT") {
              console.warn("[ImageGen] version conflict — reloading HEAD");
              handleVersionConflict();
              return;
            }
            const newVersion = Number(json?.data?.version);
            if (Number.isFinite(newVersion)) versionRef.current = newVersion;
            lastSavedImageMapRef.current = JSON.stringify(payload.map);
          } catch (err) {
            console.error("[ImageGen] Failed to save image opportunities:", err);
          }
        }
      } finally {
        imageMapInFlightPromise.current = null;
      }
    })();
    imageMapInFlightPromise.current = promise;
    return promise;
  }, [id]);

  // Merge the doc-derived map over the last known map and persist if anything
  // changed. The doc alone is NOT authoritative: between load and the deferred
  // rehydration the image nodes don't carry their attrs yet, and the other
  // content channel (original vs humanized) may hold images this doc doesn't —
  // so persisting the doc-built map verbatim wiped those entries permanently
  // (2026-07-13 incident: regenerate buttons vanished on every reload once the
  // stored map went empty). Keys are only ever ADDED or updated here; a stale
  // key for a deleted image is harmless because the map is read by src lookup.
  const persistImageOpportunitiesFromDoc = useCallback(
    (editorInstance: any, contentSnapshot: string) => {
      const merged = {
        ...imageOpportunitiesRef.current,
        ...buildImageOpportunityMap(editorInstance),
      };
      if (JSON.stringify(merged) !== lastSavedImageMapRef.current) {
        // Keep the ref current so later merges (and rehydration after a
        // channel toggle) see entries added mid-session, e.g. regenerated images.
        imageOpportunitiesRef.current = merged;
        persistImageOpportunities(merged, contentSnapshot);
      }
    },
    [persistImageOpportunities]
  );

  // Note: Scroll detection no longer needed since unlock card is embedded in content

  // Debounce the save content function with dual channels (Humanized vs Original)
  const { debounce: debouncedSaveContent, flush: flushPendingSaves } = useDualDebounce(
    (content: string, options = {}) => handleSaveContent(content, options),
    1000
  );
  // Keep the findings ref in sync so async handlers can read the freshest
  // list without depending on the React render cycle.
  useEffect(() => {
    factCheckFindingsRef.current = factCheckFindings;
  }, [factCheckFindings]);

  useEffect(() => {
    imageOpportunitiesRef.current = imageOpportunities;
  }, [imageOpportunities]);

  const turndownService = setupTurndownService();
  const editor = useEditor(
    {
      extensions: getEditorExtensions(),
      onCreate({ editor }) {
        editor.view.dom.addEventListener(
          "paste",
          (event) => {
            if (document.activeElement?.closest(".MuiTooltip-popper")) {
              event.stopPropagation();
            }
          },
          true
        );
        lastSavedContent.current = editor.getHTML();
        // Initialize word count
        setWordCount(getWordCount(editor.getText()));
      },
      content: editorContent,
      onUpdate: ({ editor }) => {
        // User typed — cached text segments are stale.
        lastPrebuiltRef.current = null;

        if (isProgrammaticChange.current) {
          return;
        }

        // Read-only sessions must never persist edits (defensive — setEditable
        // already blocks typing, but programmatic mutations could reach here).
        if (!canEditRef.current) {
          return;
        }

        // ROOT-CAUSE GUARD for the "empty article" clobber. @tiptap/react re-creates
        // the editor whenever `editorContent` changes (useEditor deps `[editorContent]`),
        // so on load the doc transitions empty→full via re-creation and onUpdate fires
        // once while the body is momentarily EMPTY — which previously persisted "" over
        // the real content through BOTH save paths below. If the editor is empty but the
        // loaded source for this channel still holds content, this is that transient
        // state (not a real edit): bail before either save. Client mirror of the backend
        // empty-overwrite guard; a true user "select-all + delete" is the data-loss case
        // we also (correctly) do not auto-persist.
        const channelSource = isEditingHumanizedContent.current
          ? humanizedHtmlRef.current
          : htmlRef.current;
        if (
          editor.getText().trim().length === 0 &&
          (channelSource || "").trim().length > 0
        ) {
          debugArticleWarn("[article-content] suppressed empty save (editor transiently empty)", {
            briefId: id,
            channel: isEditingHumanizedContent.current ? "humanized" : "content",
          });
          return;
        }

        // (Auto-hold now lives in useArticlePresence, gated on presence having
        // settled — so a lone user still auto-holds without a click, but a fresh tab
        // can no longer claim during the sync gap and silently steal an active lock.)

        const currentContent = editor.getHTML();

        // Update word count
        setWordCount(getWordCount(editor.getText()));

        if (currentContent !== lastSavedContent.current) {
          // Use our local ref to track which version we're saving
          const saveOptions = {
            isEdited: true,
            isHumanized: isEditingHumanizedContent.current ?? false,
          };

          // Debounce the save operation to avoid excessive API calls
          debouncedSaveContent(currentContent, saveOptions);
          // Don't update lastSavedContent here - wait for successful save in handleSaveContent
        }

        // Persist the image-opportunity map whenever it grows (image added or
        // regenerated). Text edits leave the map untouched, so this is a no-op
        // for ordinary typing. These events are rare, so persist IMMEDIATELY
        // (not debounced) — this single call writes both the new content (new
        // src) and the map, so a fast reload right after a regenerate can't
        // lose either.
        persistImageOpportunitiesFromDoc(editor, currentContent);
      },
    },
    [editorContent]
  );

  // Function to load content
  const getNormalizedFactCheckFindings = (data: any): FactCheckFinding[] => {
    const parseIfJson = (value: any) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizedData = unwrapDynamoValue(data);
    const candidates = [
      normalizedData?.factCheckFindings,
      normalizedData?.fact_check_findings,
      normalizedData?.factCheck?.findings,
      normalizedData?.fact_check?.findings,
      normalizedData?.factCheckResult?.findings,
      normalizedData?.fact_check_result?.findings,
      normalizedData?.factCheck,
      normalizedData?.fact_check,
    ];

    const rawFindings = candidates
      .map(parseIfJson)
      .map(unwrapDynamoValue)
      .find((entry) => Array.isArray(entry));

    if (!Array.isArray(rawFindings)) {
      return [];
    }

    return rawFindings
      .filter((item) => item && typeof item === "object")
      .map((item: any, index: number): FactCheckFinding => ({
        id: Number.isFinite(Number(item.id)) ? Number(item.id) : index + 1,
        section_heading: String(item.section_heading ?? item.sectionHeading ?? "Introduction"),
        category: String(item.category ?? "unverifiable_claim"),
        severity: String(item.severity ?? "warning"),
        original_text: String(item.original_text ?? item.originalText ?? ""),
        replacement_text:
          item.replacement_text === null || item.replacementText === null
            ? null
            : String(item.replacement_text ?? item.replacementText ?? ""),
        reasoning: String(item.reasoning ?? ""),
        source: String(item.source ?? ""),
        action: String(item.action ?? "flag_for_review"),
        applied: item.applied ?? item.status === "applied",
        status: item.status || (item.applied ? "applied" : "pending"),
        locator: item.locator,
        applied_text:
          typeof item.applied_text === "string"
            ? item.applied_text
            : typeof item.appliedText === "string"
              ? item.appliedText
              : undefined,
        applied_locator: item.applied_locator ?? item.appliedLocator ?? undefined,
        // Round-trip the agent-proposed marker fields so the load-time merge
        // can dedupe entries the backend has already persisted.
        agentProposedKey:
          typeof item.agentProposedKey === "string" ? item.agentProposedKey : undefined,
        causedBy:
          typeof item.causedBy === "number"
            ? item.causedBy
            : Number.isFinite(Number(item.causedBy))
              ? Number(item.causedBy)
              : undefined,
        evidenceUrls: Array.isArray(item.evidenceUrls) ? item.evidenceUrls : undefined,
      }))
      .filter((finding) => finding.original_text.trim().length > 0);
  };

  // Promote `factCheckAutoReview.proposedNewFindings` into the canonical
  // findings list. They become first-class findings — visible in the editor
  // (highlighted by `applyFactCheckDecorations`) and applyable via the
  // existing `/article/apply-finding` flow (the backend looks them up by id
  // in the client-supplied `currentFindings`). Idempotent across reloads:
  // once a proposed finding has been applied or declined, the backend
  // persists it inside `factCheckFindings`; the dedup below skips it on the
  // next merge.
  const mergeAgentProposedIntoFindings = (
    findings: FactCheckFinding[],
    autoReview: AutoReview | null,
  ): FactCheckFinding[] => {
    const proposed = autoReview?.proposedNewFindings ?? [];
    if (proposed.length === 0) return findings;

    // Check 1 — persisted findings (backward-compat with old "causedBy|original_text"
    // key format AND new "causedBy|section|original_text" format). Section-agnostic:
    // if any persisted finding covers this original_text, bulk-replace already
    // fixed all occurrences, so skip all proposed entries for it.
    const seenOriginal = new Set(
      findings
        .filter((f) => f.category === "agent_proposed" && typeof f.agentProposedKey === "string")
        .map((f) => {
          const parts = (f.agentProposedKey as string).split("|");
          // New format: "causedBy|section_heading|original_text" (3+ parts)
          // Strip section to get the section-agnostic key for matching.
          return parts.length >= 3
            ? `${parts[0]}|${parts.slice(2).join("|")}`
            : (f.agentProposedKey as string);
        }),
    );

    // Check 2 — within-batch dedup: same section + same text = true duplicate.
    const seenInBatch = new Set<string>();

    const baseId = findings.reduce((m, f) => Math.max(m, f.id ?? 0), 0);
    const merged = [...findings];

    proposed.forEach((p, idx) => {
      const originalKey = `${p.causedBy ?? "x"}|${p.original_text}`;
      if (seenOriginal.has(originalKey)) return;

      const key = `${p.causedBy ?? "x"}|${p.section_heading}|${p.original_text}`;
      if (seenInBatch.has(key)) return;
      seenInBatch.add(key);
      merged.push({
        id: baseId + 1 + idx,
        section_heading: p.section_heading,
        category: "agent_proposed",
        severity: p.severity,
        original_text: p.original_text,
        replacement_text: p.replacement_text || null,
        reasoning: p.reasoning,
        source: "",
        action: p.action,
        status: "pending",
        agentProposedKey: key,
        causedBy: p.causedBy,
        evidenceUrls: Array.isArray(p.evidenceUrls)
          ? p.evidenceUrls.map((u: string) => (u.startsWith("http") ? u : `https://${u}`))
          : [],
      });
    });

    return merged;
  };

  const getNormalizedFactCheckAutoReview = (data: any): AutoReview | null => {
    const parseIfJson = (value: any) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizedData = unwrapDynamoValue(data);
    const candidates = [
      normalizedData?.factCheckAutoReview,
      normalizedData?.fact_check_auto_review,
      normalizedData?.factCheckReview,
    ];

    const raw = candidates
      .map(parseIfJson)
      .map(unwrapDynamoValue)
      .find((entry) => entry && typeof entry === "object" && !Array.isArray(entry));

    if (!raw || typeof raw !== "object") return null;

    return {
      status: raw.status,
      completedAt: raw.completedAt,
      decisions: Array.isArray(raw.decisions) ? raw.decisions : [],
      proposedNewFindings: Array.isArray(raw.proposedNewFindings) ? raw.proposedNewFindings : [],
      summary: raw.summary || {},
    };
  };

  // Read the persisted src -> opportunity map saved alongside the article.
  // Handles string-encoded JSON and DynamoDB-wrapped shapes, mirroring the
  // fact-check normalizers above.
  const getNormalizedImageOpportunities = (data: any): Record<string, any> => {
    const parseIfJson = (value: any) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizedData = unwrapDynamoValue(data);
    const candidates = [
      normalizedData?.imageOpportunities,
      normalizedData?.image_opportunities,
    ];

    const raw = candidates
      .map(parseIfJson)
      .map(unwrapDynamoValue)
      .find((entry) => entry && typeof entry === "object" && !Array.isArray(entry));

    if (!raw || typeof raw !== "object") return {};

    // Keep only entries that look like an opportunity object keyed by a URL.
    const map: Record<string, any> = {};
    Object.entries(raw).forEach(([src, opportunity]) => {
      if (src && opportunity && typeof opportunity === "object") {
        map[src] = opportunity;
      }
    });
    return map;
  };

  const loadContent = async () => {
    try {
      // Reset loaded flag to prevent effects from running with stale refs during fetch
      isContentLoaded.current = false;
      setIsLoading(true);
      const data = await fetchContent(id);

      // Process the content based on the data format returned
      if (data) {
        // Capture the optimistic-concurrency baseline for subsequent writes.
        // Coerce with Number() — the backend may serialize the DynamoDB Decimal as
        // a string, and a strict typeof check would wrongly reset us to 0 (→ 409 loop).
        const loadedVersion = Number(data.version);
        versionRef.current = Number.isFinite(loadedVersion) ? loadedVersion : 0;

        // [article-content] Load probe: distinguishes "DB genuinely empty on the
        // shown channel" (toggle issue) from "data present but editor renders empty"
        // (render issue). Greppable prefix; see also handleSaveContent / backend.
        const _contentLen = (data.content || "").length;
        const _humanizedLen = (data.humanizedContent || "").length;
        const _shownChannel = data.isHumanized ? "humanized" : "content";
        debugArticle("[article-content] load", {
          briefId: id,
          version: data.version,
          contentLen: _contentLen,
          humanizedLen: _humanizedLen,
          isHumanized: data.isHumanized,
          shownChannel: _shownChannel,
          shownLen: data.isHumanized ? _humanizedLen : _contentLen,
        });

        const contentHtml = converter.makeHtml(data.content || "");
        const humanizedHtml = converter.makeHtml(data.humanizedContent || "");

        setHtml(contentHtml);
        setHumanizedHtml(humanizedHtml);
        htmlRef.current = contentHtml;
        humanizedHtmlRef.current = humanizedHtml;
        isContentLoaded.current = true;

        // Set both states based on the data
        dispatch(setIsHumanized(data.isHumanized));
        dispatch(setShowHumanizeContent(data.isHumanized));

        // Initialize editing state
        isEditingHumanizedContent.current = data.isHumanized;
        setIsEditingHumanizedVersion(data.isHumanized);

        // Set the initial content
        const initialContent = data.isHumanized ? humanizedHtml : contentHtml;

        // Initialize word count from HTML content (which will be displayed in editor)
        // Create a temporary div to extract text from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = initialContent;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        const initialWordCount = getWordCount(textContent);
        setWordCount(initialWordCount);

        // Set the initial content - truncation will be handled in the truncation effect
        setEditorContent(initialContent);
        handleSaveEvaluationData(data);

        // Set the article link
        if (data.articleLink) {
          setArticleLink(data.articleLink);
        }

        const baseFindings = getNormalizedFactCheckFindings(data);
        const autoReview = getNormalizedFactCheckAutoReview(data);
        setFactCheckFindings(mergeAgentProposedIntoFindings(baseFindings, autoReview));
        setFactCheckAutoReview(autoReview);

        // Load the persisted image-opportunity map and seed the "last saved"
        // ref so the first edit/rehydration doesn't trigger a redundant save.
        const imageMap = getNormalizedImageOpportunities(data);
        lastSavedImageMapRef.current = JSON.stringify(imageMap);
        setImageOpportunities(imageMap);
      }
    } catch (error) {
      dispatch(
        snackAdded({
          message: `Failed to load content`,
          type: "error",
        })
      );
      setFactCheckFindings([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Shared optimistic-concurrency conflict handler: another writer (human or
  // Hermes) advanced the article, so reload HEAD instead of clobbering it.
  // When the conflict came from a CONTENT write, `rejected` carries the edit that
  // was refused — we buffer it so the user can restore it rather than lose it
  // (the reload below would otherwise overwrite the editor with HEAD).
  const handleVersionConflict = (rejected?: { html: string; isHumanized: boolean }) => {
    const hasEdit = !!rejected && typeof rejected.html === "string" && rejected.html.trim().length > 0;
    if (hasEdit) setPendingConflictEdit(rejected!);
    dispatch(
      snackAdded({
        message: hasEdit
          ? "This article changed while you were editing — your latest change wasn't saved. Loaded the current version; use “Restore my changes” to re-apply it."
          : "Someone else updated this article — reloading the latest version.",
        type: "warning",
        duration: 8000,
      })
    );
    loadContent();
  };

  // Put the user's refused edit back into the editor (on its channel) after a
  // conflict reload, so they can review it against the current version and re-save
  // — the re-save now carries the freshly-loaded version and will succeed. This is
  // an explicit user choice to keep their version, so it never silently clobbers.
  const restorePendingConflictEdit = () => {
    const pending = pendingConflictEdit;
    if (!pending) return;
    // Ensure the channel the edit was made on is the one being shown.
    dispatch(setIsHumanized(pending.isHumanized));
    dispatch(setShowHumanizeContent(pending.isHumanized));
    isEditingHumanizedContent.current = pending.isHumanized;
    setIsEditingHumanizedVersion(pending.isHumanized);
    if (pending.isHumanized) {
      setHumanizedHtml(pending.html);
      humanizedHtmlRef.current = pending.html;
    } else {
      setHtml(pending.html);
      htmlRef.current = pending.html;
    }
    setEditorContent(pending.html);
    setPendingConflictEdit(null);
    dispatch(
      snackAdded({
        message: "Your changes are back in the editor — review and save.",
        type: "info",
      })
    );
  };

  // Backend-enforced take-over, used for EVERY holder kind (human or agent). Hits
  // /article/take-over, which bumps the article version (so the previous holder's
  // next save 409s and reloads) and, for an agent, force-releases its Control-Plane
  // review lock. Then reload HEAD so the taking-over user edits current content.
  //
  // Fails CLOSED: if the backend take-over rejects (the thunk now rejects on non-200
  // / network error), we surface a snack and RE-THROW — the caller (requestTakeOver)
  // will not grant edit control, so we never hand the editor to a session that
  // didn't actually win the lock on the backend.
  const forceTakeOverAndReload = async () => {
    try {
      const res: any = await dispatch(takeOverArticle({ briefId: id })).unwrap();
      // Seed our version baseline from the take-over response so the first save
      // after taking over carries the freshly-bumped version (avoids an immediate
      // self-409). loadContent below also refreshes it from HEAD.
      const v = Number(res?.data?.version);
      if (Number.isFinite(v)) versionRef.current = v;
    } catch (e) {
      dispatch(
        snackAdded({
          message: "Couldn't take over the article — please try again.",
          type: "error",
        })
      );
      throw e; // fail closed — requestTakeOver must not grant control
    }
    await loadContent();
    // We just reloaded HEAD; the promotion effect that fires when isHolder flips true
    // (right after this resolves and requestTakeOver calls takeOver) must NOT reload again.
    suppressPromotionReloadRef.current = true;
  };

  const handleSaveEvaluationData = async (data: any) => {
    // Process and validate the evaluationData
    try {
      let isValid = false;

      if (data.articleEEATScore) {
        try {
          // Get articles and analysis
          let articles = data.articleEEATScore.articles;
          let analysis = data.articleEEATScore.analysis;
          let suggestions = data.articleEEATScore.suggestions;

          // Parse if needed
          const parsedArticles =
            typeof articles === "string" ? JSON.parse(articles) : articles;
          const parsedAnalysis =
            typeof analysis === "string" ? JSON.parse(analysis) : analysis;

          // Check for required structure in articles
          if (
            !parsedArticles?.original?.scores ||
            !parsedArticles?.updated?.scores ||
            !parsedArticles?.original?.overall ||
            !parsedArticles?.updated?.overall
          ) {
            throw new Error("Articles data is missing required structure");
          }

          // Verify scores contain all required metrics
          const originalScores = parsedArticles.original.scores;
          const updatedScores = parsedArticles.updated.scores;

          if (
            typeof originalScores.experience !== "number" ||
            typeof originalScores.expertise !== "number" ||
            typeof originalScores.authoritativeness !== "number" ||
            typeof originalScores.trustworthiness !== "number"
          ) {
            throw new Error("Original scores are missing required metrics");
          }

          if (
            typeof updatedScores.experience !== "number" ||
            typeof updatedScores.expertise !== "number" ||
            typeof updatedScores.authoritativeness !== "number" ||
            typeof updatedScores.trustworthiness !== "number"
          ) {
            throw new Error("Updated scores are missing required metrics");
          }

          // Check analysis structure
          if (
            !parsedAnalysis?.experience?.comparison ||
            !parsedAnalysis?.expertise?.comparison ||
            !parsedAnalysis?.authoritativeness?.comparison ||
            !parsedAnalysis?.trustworthiness?.comparison ||
            !Array.isArray(parsedAnalysis?.summary)
          ) {
            throw new Error("Analysis is missing required structure");
          }

          // If we reach here without error, data is valid
          isValid = true;
        } catch (error) {
          console.error("Invalid EEAT score data:", error);
          isValid = false;
        }
      } else {
        isValid = true;
      }

      setEvaluationData({ articleEEATScore: data.articleEEATScore });
      setIsValidEvaluationData(isValid);
    } catch (error) {
      console.error("Failed to save evaluation data:", error);
    }
  };

  function convertToMarkdown(content: string) {
    // Add color span rule
    turndownService.addRule("colorSpan", {
      filter: function (node) {
        return node.nodeName === "SPAN" && node.style && node.style.color;
      },
      replacement: function (content, node) {
        const color = node.style.color;
        return `<span style="color: ${color};">${content}</span>`;
      },
    });

    return turndownService.turndown(content);
  }
  // Function to save content
  // Public entry point: serialize every content save through a single chain so no
  // two /article/update requests are ever in flight together (see saveChainRef).
  const handleSaveContent = (content: string, options: any): Promise<any> => {
    const run = saveChainRef.current
      .catch(() => {}) // a previous save's failure must not wedge the queue
      .then(() => doSaveContent(content, options));
    saveChainRef.current = run;
    return run;
  };

  const doSaveContent = async (content: string, options: any): Promise<any> => {
    // Mark that we are currently saving so other updates can be deferred
    isContentSaving.current = true;

    const markdownContent = convertToMarkdown(content);

    // Calculate character count from markdown content
    const charCount = markdownContent.length;

    // [article-content] Save probe: which channel + how big a body we're about to
    // persist (debug-flag gated).
    const _channel = options?.isHumanized ? "humanized" : "content";
    if (markdownContent.trim().length === 0) {
      debugArticleWarn("[article-content] EMPTY save attempt", {
        briefId: id,
        channel: _channel,
        lastKnownVersion: versionRef.current,
      });
    } else {
      debugArticle("[article-content] save", {
        briefId: id,
        channel: _channel,
        markdownLen: markdownContent.length,
        lastKnownVersion: versionRef.current,
      });
    }

    try {
      const result = await saveContent(markdownContent, {
        ...options,
        charCount,
        lastKnownVersion: versionRef.current,
      });

      // Optimistic-concurrency conflict. A 409 means another writer advanced the
      // article since our baseline — ALWAYS reload HEAD rather than replay our
      // (now stale) body. We deliberately do NOT adopt the server version and retry:
      // that could clobber the newer content, and the load-time empty-save guard has
      // already removed the stale-ref case that previously justified a retry.
      if (result?.status_code === 409 || result?.data?.code === "VERSION_CONFLICT") {
        // Buffer the refused edit so the reload doesn't silently lose it.
        handleVersionConflict({ html: content, isHumanized: !!options?.isHumanized });
        return result;
      }

      // Advance our version baseline for the next write (coerce — see loadContent).
      const newVersion = Number(result?.data?.version);
      if (Number.isFinite(newVersion)) {
        versionRef.current = newVersion;
      }

      // Update refs only (not state) to avoid triggering effects that reset cursor
      if (options.isHumanized) {
        humanizedHtmlRef.current = content;
      } else {
        htmlRef.current = content;
      }

      // Update the reference of the last successfully saved content
      lastSavedContent.current = content;

      return result;
    } catch (error) {
      dispatch(
        snackAdded({
          message: "Failed to save changes",
          type: "error",
        })
      );
      throw error;
    } finally {
      // Reset the saving flag regardless of success or failure
      isContentSaving.current = false;
    }
  };

  // Load content when component mounts or ID changes
  useEffect(() => {
    if (id) {
      loadContent();
    }
  }, [id]);

  // ── Single-writer lock (presence-coordinated) ───────────────────────────
  // Toggle TipTap editability with our holder status (or presence being off).
  useEffect(() => {
    if (editor) editor.setEditable(presence.canEdit);
  }, [editor, presence.canEdit]);

  // Register the "reload HEAD before control" handler the navbar's Take Over
  // button invokes (see ArticlePresenceContext.requestTakeOver). Route EVERY
  // take-over (human OR agent) through the backend so the previous holder's version
  // is bumped and their pending saves 409 — human-to-human take-over is now
  // backend-enforced, not just a UI election. Throws on failure → control not granted.
  const registerTakeOverReload = presence.registerTakeOverReload;
  useEffect(() => {
    registerTakeOverReload(async () => {
      await forceTakeOverAndReload();
    });
    return () => registerTakeOverReload(null);
  }, [registerTakeOverReload]);

  // Notify a demoted holder that someone took over (now read-only).
  const prevIsHolderRef = React.useRef(presence.isHolder);
  useEffect(() => {
    if (prevIsHolderRef.current && !presence.isHolder && presence.active) {
      dispatch(
        snackAdded({
          message: `${presence.holder?.displayName || "Someone"} took over editing — you're now read-only.`,
          type: "info",
        })
      );
    }
    prevIsHolderRef.current = presence.isHolder;
  }, [presence.isHolder, presence.active]);

  // Refresh-on-promotion: when a read-only viewer becomes the editor because the
  // previous holder LEFT (their tab closed → presence expired → we auto-hold the lock),
  // our loaded content is whatever we last fetched — i.e. stale, missing their edits.
  // Reload HEAD so the newly-promoted editor sees the latest before typing. The explicit
  // Take Over path already reloads, so it sets `suppressPromotionReloadRef` to avoid a
  // redundant double-load; the lone-user initial auto-hold has no prior holder and is
  // skipped by the `prevHolder` guard.
  const prevHolderSidRef = React.useRef<string | null>(null);
  const suppressPromotionReloadRef = React.useRef(false);
  useEffect(() => {
    const prevHolderSid = prevHolderSidRef.current;
    const currHolderSid = presence.holder?.sessionId ?? null;
    const promotedFromAnotherHolder =
      presence.active &&
      presence.isHolder &&
      !!prevHolderSid && // someone held it just before…
      prevHolderSid !== currHolderSid; // …and that someone wasn't us (now it is)
    if (promotedFromAnotherHolder) {
      if (suppressPromotionReloadRef.current) {
        suppressPromotionReloadRef.current = false; // explicit take-over already reloaded
      } else {
        debugArticle("[article-content] promoted to editor — reloading HEAD", {
          briefId: id,
          previousHolder: prevHolderSid,
        });
        loadContent();
      }
    }
    prevHolderSidRef.current = currHolderSid;
  }, [presence.holder, presence.isHolder, presence.active]);

  // Handle content truncation for preview mode
  useEffect(() => {
    if (!editor || !hideArticle) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Wait for content to be loaded (use ref instead of state to avoid re-triggering on save)
    if (!isContentLoaded.current) {
      return;
    }

    const content = editor.getHTML();

    if (content.length < 100) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Use TipTap commands to insert unlock card after 4 paragraphs
    setUseScrollBasedTruncation(true);

    isProgrammaticChange.current = true;
    insertUnlockCardWithTipTap(editor, content, isOptimizedMode);
    isProgrammaticChange.current = false;
    setTruncatedContent(content);
  }, [hideArticle, editor]);

  // Rehydrate image nodes from the persisted opportunity map after a load.
  // Markdown can't carry the per-image metadata, so we restore it (plus the
  // article id) onto the matching image nodes by src — re-enabling the
  // per-image "regenerate" control across reloads.
  useEffect(() => {
    if (!editor) return;

    // Defer to rAF so this runs AFTER TipTap commits the (re-created) editor's
    // parsed doc. @tiptap/react re-creates the editor when editorContent
    // changes; without the defer this effect can run before the loaded
    // markdown is parsed into image nodes, so descendants() would match
    // nothing and the buttons would never re-attach.
    const raf = requestAnimationFrame(() => {
      const map = imageOpportunitiesRef.current;
      if (!editor || !map || Object.keys(map).length === 0) return;

      const { state, view } = editor;
      let tr = state.tr;
      let changed = false;
      state.doc.descendants((node: any, pos: number) => {
        if (node.type.name !== "image" || !node.attrs?.src) return;
        const opportunity = map[node.attrs.src];
        if (!opportunity) return;
        if (node.attrs.imageOpportunity && node.attrs.articleId === id) return; // already set
        tr = tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          imageOpportunity: opportunity,
          articleId: id,
        });
        changed = true;
      });

      if (changed) {
        // Programmatic — don't let the resulting transaction trigger a save.
        isProgrammaticChange.current = true;
        view.dispatch(tr);
        isProgrammaticChange.current = false;
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [editor, imageOpportunities, editorContent, id]);


  // Helper functions
  const selectedWords = () => {
    return countSelectedWords(editor);
  };

  const handleAddMetaToArticle = (html: string) => {
    return addMetaToArticle(html, title, description);
  };

  const insertImageIntoEditor = (
    editorInstance: any,
    imageUrl: string,
    altText?: string,
    placementContext?: { method: string; identifier: string },
    isCover?: boolean,
    imageOpportunity?: Record<string, any>,
    articleId?: string
  ) => {
    if (!editorInstance) return;

    // Extra attributes persisted on the image node so a single image can later
    // be regenerated with user feedback (see CustomImage extension).
    const extraAttrs = {
      ...(articleId ? { articleId } : {}),
      ...(imageOpportunity ? { imageOpportunity } : {}),
    };

    // Cover image: insert before the first H1
    if (isCover) {
      let h1Pos: number | null = null;
      editorInstance.state.doc.descendants((node: any, pos: number) => {
        if (h1Pos !== null) return false;
        if (node.type.name === "heading" && node.attrs.level === 1) {
          h1Pos = pos;
          return false;
        }
      });
      if (h1Pos !== null) {
        editorInstance
          .chain()
          .focus()
          .insertContentAt(h1Pos, {
            type: "image",
            attrs: { src: imageUrl, alt: altText || "", ...extraAttrs },
          })
          .run();
        return;
      }
    }

    // Contextual placement based on identifier
    if (placementContext?.identifier) {
      let targetPos: number | null = null;
      const needle = placementContext.identifier.trim().toLowerCase();
      const isHeading = placementContext.method === "inline_after_heading";
      const targetType = isHeading ? "heading" : "paragraph";

      editorInstance.state.doc.descendants((node: any, pos: number) => {
        if (targetPos !== null) return false;
        if (
          node.type.name === targetType &&
          node.textContent.trim().toLowerCase().includes(needle)
        ) {
          targetPos = pos + node.nodeSize;
          return false;
        }
      });

      if (targetPos !== null) {
        editorInstance
          .chain()
          .focus()
          .setTextSelection(targetPos)
          .setImage({ src: imageUrl, alt: altText || "", ...extraAttrs })
          .run();
        return;
      }
    }

    // Fallback: insert at end of document
    const endPos = editorInstance.state.doc.content.size;
    editorInstance
      .chain()
      .focus()
      .setTextSelection(endPos)
      .setImage({ src: imageUrl, alt: altText || "", ...extraAttrs })
      .run();
  };

  const handleGenerateImages = async () => {
    if (!canEditRef.current) return; // read-only sessions can't generate/insert images
    setShowImageGenModal(true);
    setImageGenLoading(true);
    setImageGenError(null);

    try {
      const articleContent = editor?.getHTML();
      if (!articleContent) {
        setImageGenError("No article content to analyze");
        setImageGenLoading(false);
        return;
      }

      // Fetch brand guidelines + cover aspect ratio for the project (if available)
      const { guidelines: brandGuidelines, coverAspectRatio } =
        await fetchProjectImageSettings(id);

      // Call the image-generation Function URL directly (IAM-signed) to avoid
      // the API Gateway 29s timeout.
      const { ok, data: result } = await signedLambdaFetch(
        process.env.NEXT_PUBLIC_IMAGE_GENERATION_URL!,
        {
          article_content: articleContent,
          article_id: id,
          article_url: `article-${id}`,
          brand_style_guidelines: brandGuidelines,
          cover_aspect_ratio: coverAspectRatio,
        }
      );

      if (ok && result.opportunities) {
        // Map the response to match the modal's expected format
        // New structure: each opportunity has images[] array + image_url
        // The Lambda generates one image per opportunity, with the URL at opp.image_url
        // Each opp.images[] entry is a prompt definition — they share the same generated image_url
        const images: any[] = [];
        const opps = result.opportunities || [];

        for (const opp of opps) {
          const oppImages = opp.images || [opp]; // fallback for old flat format
          const imageUrl = opp.image_url || "";

          for (const img of oppImages) {
            // Full generation context for this image — persisted on the node so
            // it can later be regenerated with a user comment.
            const opportunity = {
              image_prompt: img.image_prompt || "",
              goal_statement: img.goal_statement || "",
              alt_text: img.alt_text || "",
              image_type: img.image_type || "",
              text_in_image: img.text_in_image || "",
              aspect_ratio: img.aspect_ratio || "",
              placement_context: img.placement_context || null,
              is_cover: img.is_cover || false,
              brand_execution_contract:
                img.brand_execution_contract || null,
              visual_evidence_contract:
                img.visual_evidence_contract || null,
            };
            images.push({
              success: !!imageUrl,
              image_url: imageUrl,
              opportunity,
              metadata: {
                alt_text: img.alt_text || "",
                image_type: img.image_type || "",
                placement_context: img.placement_context || null,
                is_cover: img.is_cover || false,
                text_in_image: img.text_in_image || "",
                aspect_ratio: img.aspect_ratio || "",
              },
            });
          }
        }
        // Auto-insert all successful images into the editor
        const successfulImages = images.filter((img: any) => img.success && img.image_url);

        if (successfulImages.length > 0 && editor) {
          // Insert cover images first, then others
          const coverImages = successfulImages.filter((img: any) => img.metadata?.is_cover);
          const otherImages = successfulImages.filter((img: any) => !img.metadata?.is_cover);

          for (const img of [...coverImages, ...otherImages]) {
            insertImageIntoEditor(
              editor,
              img.image_url,
              img.metadata?.alt_text,
              img.metadata?.placement_context,
              img.metadata?.is_cover,
              img.opportunity,
              id
            );
          }

          // Persist the new src -> opportunity map immediately so the
          // regenerate control survives a reload even if the user never edits.
          persistImageOpportunitiesFromDoc(editor, editor.getHTML());
        }

        setShowImageGenModal(false);
      } else {
        setImageGenError(result.error || "Image generation failed");
      }
    } catch (err: any) {
      console.error("Image generation error:", err);
      setImageGenError(err.message || "Failed to generate images");
    } finally {
      setImageGenLoading(false);
    }
  };

  const handleDownload = () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    let htmlToDownload = editor?.getHTML();
    const htmlWithMeta = handleAddMetaToArticle(htmlToDownload);
    Export2Docx(htmlWithMeta, title);

    // Reset download flag if using the optimized editor
    if (additionalProps.setIsDownloadContent) {
      additionalProps.setIsDownloadContent(false);
    }
  };

  const handleCopy = async () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    try {
      let htmlToCopy = editor?.getHTML();
      const htmlWithMeta = handleAddMetaToArticle(htmlToCopy);

      const success = await copyHtmlToClipboard(htmlWithMeta);
      if (success) {
        dispatch(
          snackAdded({
            message: "Content copied to clipboard!",
            type: "success",
            duration: 2000,
          })
        );
      } else {
        throw new Error("Clipboard copy failed");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      dispatch(
        snackAdded({
          message: "Failed to copy content. Please try again.",
          type: "error",
          duration: 1000,
        })
      );
    }

    // Reset copy flag if using the optimized editor
    if (additionalProps.setIsCopyContent) {
      additionalProps.setIsCopyContent(false);
    }
  };

  const handleAIDropdown = () => {
    if (isEEATMode) {
      dispatch(
        snackAdded({
          message:
            "You can't apply AI to an EEAT article. Please use Update Article to improve your article.",
          type: "info",
        })
      );
      return;
    }
    isProgrammaticChange.current = true;
    const { from, to } = editor.state.selection;
    editor
      ?.chain()
      .setTextSelection({ from, to })
      .focus()
      .setHighlight("lightgray")
      .run();
    isProgrammaticChange.current = false;

    let startCoords = editor.view.coordsAtPos(from);
    let endCoords = editor.view.coordsAtPos(to);

    // Set coordinates for the dropdown
    // Position it slightly below and to the left of the selected text
    // Use the end of the selection (where the cursor would be) as the anchor point
    setCords({
      top: endCoords.bottom + 10, // 10px below the selection
      left: endCoords.left - 20, // 20px to the left of the end of selection
      bottom: endCoords.bottom,
      right: endCoords.right,
    });

    if (!scrollContainerRef.current) {
      return;
    }

    // Find all possible scroll sources and use the maximum value
    const editorDOM = editor.view.dom;

    // Get all possible scroll values
    const windowScrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;
    const containerScrollTop = scrollContainerRef.current.scrollTop || 0;
    const editorScrollTop = editorDOM.scrollTop || 0;

    // Check for scrollable parent elements
    let parentScrollTop = 0;
    let currentElement = editorDOM.parentElement;
    let scrollableParent = null;

    while (currentElement && currentElement !== document.body) {
      if (
        currentElement.scrollHeight > currentElement.clientHeight &&
        currentElement.scrollTop > 0
      ) {
        scrollableParent = currentElement;
        parentScrollTop = currentElement.scrollTop;

        break;
      }
      currentElement = currentElement.parentElement;
    }

    // Use the maximum scroll value from any source
    const scrollY = Math.max(
      windowScrollY,
      containerScrollTop,
      editorScrollTop,
      parentScrollTop
    );

    // Store the effective scroll position for use in dropdown positioning
    const effectiveScrollPosition = scrollY;

    // Calculate position relative to the editor's position in the container
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const editorRect = editor.view.dom.getBoundingClientRect();

    // We will position the dropdown at the beginning of the last line of selected text
    // Use a safer approach to get text content
    const selectionText = editor.state.doc.textBetween(from, to);

    // Find the last newline in the selection
    const lastNewlineIndex = selectionText.lastIndexOf("\n");

    // Position at the start of the last line
    let lastLineStartPos = from;
    if (lastNewlineIndex !== -1) {
      // Found a newline, position after it (first character of last line)
      lastLineStartPos = from + lastNewlineIndex + 1;
    }

    // Get coordinates for the start of the last line
    let startOfLastLineCoords = editor.view.coordsAtPos(lastLineStartPos);

    // Calculate available space below the selection
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - endCoords.bottom;

    // Height of the dropdown (approximate)
    const dropdownHeight = 150; // Adjust based on your dropdown's actual height

    // Check if there's enough space below
    const positionAbove = spaceBelow < dropdownHeight;

    let relativeTop, relativeLeft;

    if (positionAbove) {
      // Position above the selection
      relativeTop =
        startOfLastLineCoords.top -
        containerRect.top +
        scrollY -
        dropdownHeight;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    } else {
      // Position below the selection (original behavior)
      relativeTop = startOfLastLineCoords.top - containerRect.top + scrollY;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    }

    // Set coordinates for the dropdown
    setCords({
      top: positionAbove ? relativeTop : relativeTop + 10, // If above, no extra padding
      left: relativeLeft,
      bottom: relativeTop,
      right: endCoords.right - containerRect.left,
    });

    // Extract selected blocks
    const selectedBlocks = extractSelectedBlocks(
      editor,
      editor.state.selection.from,
      editor.state.selection.to,
      converter
    );

    if (selectedWords() > 500) {
      dispatch(
        snackAdded({
          message: "Please select less than 500 words",
          type: "error",
        })
      );
      editor?.commands.unsetHighlight();
      return;
    }

    // Set state for dropdown
    setShowAIDropdown(true);
    setShouldShowBubbleMenu(false);
    setSelectedText(selectedBlocks);

    // Focus and scroll to the element
    setTimeout(() => {
      try {
        // Try direct window scrolling as a backup
        const editorRect = scrollContainerRef.current
          ?.querySelector(".ProseMirror")
          ?.getBoundingClientRect();
        if (editorRect) {
          // Use a simpler approach - just scroll to a fixed offset from the selection
          // This is more reliable across different scroll positions
          const targetScroll = Math.max(0, relativeTop - 150);

          // Find the scrollable container
          let scrollContainer = null;
          let currentEl = editorDOM;

          while (currentEl && currentEl !== document.body) {
            if (currentEl.scrollHeight > currentEl.clientHeight) {
              scrollContainer = currentEl;
              break;
            }
            currentEl = currentEl.parentElement;
          }

          // If we found a scrollable container, scroll it
          if (scrollContainer) {
            scrollContainer.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          } else {
            window.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          }
        }
      } catch (error) {
        console.error("Error during focus/scroll:", error);
      }
    }, 100);
  };

  const handleCloseAIDropdown = () => {
    setShowAIDropdown(false);
    setShouldShowBubbleMenu(true);
    setSelectedText([]);
    setCords({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    });

    // Remove the highlight
    editor?.commands.unsetHighlight();
    setBottomMargin(0);
  };

  const handleSelectFactCheckFinding = useCallback((finding: FactCheckFinding) => {
    if (!editor) return;
    // Applied findings should scroll to the post-apply text; pending to the original.
    const isApplied = (finding.status || (finding.applied ? "applied" : "pending")) === "applied";
    const range = isApplied
      ? findRangeForAppliedFinding(editor, finding, lastPrebuiltRef.current ?? undefined)
      : findRangeForFinding(
          editor,
          finding,
          lastPrebuiltRef.current ?? undefined,
          localOffsetsDirtyRef.current
        );
    if (!range) {
      dispatch(
        snackAdded({
          message: "Could not find this text in the current editor version.",
          type: "warning",
        })
      );
      return;
    }

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .run();

    // Smooth-scroll to the selected text (TipTap's scrollIntoView is a jump)
    requestAnimationFrame(() => {
      try {
        const { node } = editor.view.domAtPos(range.from);
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {}
    });
  }, [dispatch, editor]);

  const handleApplyFactCheckAction = useCallback(
    async (finding: FactCheckFinding) => {
      if (!editor || !id) return;
      if (!canEditRef.current) return; // read-only sessions can't mutate content

      // Re-entrancy guard: a second apply (or a decline) firing while this
      // call is in flight can race the response and clobber it. The sidebar
      // disables its buttons via isApplyingFactCheck, but check the ref too
      // in case any other surface invokes the handler.
      if (isApplyingFactCheckRef.current) return;
      isApplyingFactCheckRef.current = true;
      setIsApplyingFactCheck(true);

      try {
        // Drain BOTH save queues before we apply. Either a content save or
        // a fact-check status save (e.g. a decline that just fired) can land
        // *after* /article/apply-finding and clobber the applied markdown
        // with pre-apply content. Both flush helpers await any in-flight HTTP.
        // Inside the try so a flush rejection still releases the lock.
        await Promise.all([flushPendingSaves(), flushPendingFindingsSave()]);
        const currentHtml = editor.getHTML();
        const currentMarkdown = convertToMarkdown(currentHtml);
        const isHumanizedNow = isEditingHumanizedContent.current ?? false;

        const result = await dispatch(
          applyFactCheckFinding({
            briefId: id,
            isHumanized: isHumanizedNow,
            currentMarkdown,
            // Use the ref, not state — flushPendingFindingsSave may have just
            // drained pending status changes that haven't re-rendered yet,
            // so the React state closure could still hold the older list.
            currentFindings: factCheckFindingsRef.current,
            finding,
            lastKnownVersion: versionRef.current,
          })
        ).unwrap();

        // Apply-finding bumped the article version — advance our baseline (coerce).
        const appliedVersion = Number(result.version);
        if (Number.isFinite(appliedVersion)) {
          versionRef.current = appliedVersion;
        }

        // Render the canonical markdown the backend just persisted.
        const html = converter.makeHtml(result.updatedMarkdown || "");

        isProgrammaticChange.current = true;
        try {
          editor.commands.setContent(html);
        } finally {
          isProgrammaticChange.current = false;
        }

        // Backend already saved this content — sync the local refs so the
        // next user edit is correctly recognized as the first unsaved change.
        lastSavedContent.current = html;
        if (isHumanizedNow) {
          humanizedHtmlRef.current = html;
        } else {
          htmlRef.current = html;
        }

        // Replace local findings with the backend's authoritative set.
        setFactCheckFindings(result.updatedFindings as FactCheckFinding[]);

        // The cached resolver segments belong to a stale doc — drop them.
        lastPrebuiltRef.current = null;
        localOffsetsDirtyRef.current = true;

        const action = String(finding.action || "").trim().toLowerCase();
        const successMessage =
          action === "remove"
            ? "Text removed."
            : action === "flag_for_review" && !finding.replacement_text?.trim()
              ? "Marked as reviewed."
              : "Replacement applied.";
        dispatch(snackAdded({ message: successMessage, type: "success" }));
        setActiveFindingId(finding.id);
      } catch (err: any) {
        const code: string | undefined = err?.code;
        // Stale apply against an article someone else advanced — reload HEAD.
        if (code === "VERSION_CONFLICT") {
          handleVersionConflict();
          return;
        }
        const message =
          code === "TEXT_NOT_FOUND"
            ? "Could not locate this text in the article. It may have been edited."
            : code === "AMBIGUOUS_MATCH"
              ? "Multiple matches found — please edit manually to clarify."
              : code === "FINDING_NOT_FOUND"
                ? "This finding is no longer in the current list. Please refresh."
                : err?.message || "Failed to apply finding.";
        dispatch(snackAdded({ message, type: "warning" }));
      } finally {
        isApplyingFactCheckRef.current = false;
        setIsApplyingFactCheck(false);
      }
    },
    [dispatch, editor, id, converter, flushPendingSaves, flushPendingFindingsSave]
  );

  const handleDeclineFactCheckAction = useCallback((finding: FactCheckFinding) => {
    // Don't mutate findings during an in-flight apply — a decline-driven
    // /article/update with pre-apply markdown could land after the apply
    // response and overwrite it. The sidebar already greys out the button,
    // but enforce here too in case any other surface invokes the handler.
    if (isApplyingFactCheckRef.current) return;
    setFactCheckFindings((prev) => {
      const updated = prev.map((item) =>
        item.id === finding.id
          ? { ...item, status: "declined" as const }
          : item
      );
      saveFactCheckFindings(updated, editor?.getHTML() || lastSavedContent.current);
      return updated;
    });
    dispatch(snackAdded({ message: "Suggestion declined.", type: "info" }));
  }, [dispatch, saveFactCheckFindings]);

  const applyFactCheckDecorations = useCallback(() => {
    if (!editor) return;
    const runId = ++decorationRunId.current;

    if (factCheckFindings.length === 0) {
      (editor.commands as any).clearFactCheckRanges?.();
      return;
    }

    // Build text segments once and share across all findings to avoid
    // re-walking the ProseMirror doc for each finding.
    const segmentData = buildTextSegments(editor);
    const prebuilt: PrebuiltSegments = {
      segments: segmentData.segments,
      combinedText: normalizeUnicode(segmentData.combinedText),
    };
    lastPrebuiltRef.current = prebuilt;
    // Skip declined findings — no underline for dismissed suggestions
    const activeFindings = factCheckFindings.filter((f) => f.status !== "declined");
    const results: Array<FactCheckDecorationRange | null> = activeFindings.map((finding) => {
      const effectiveStatus = finding.status || (finding.applied ? "applied" : "pending");
      // Pending findings underline the original_text in place.
      // Applied findings green-highlight the applied_text (preserved post-apply
      // separately from the audit-only original_text). If applied_text is
      // missing or no longer matches the doc, gracefully drop the decoration —
      // the sidebar still shows the finding as Applied.
      const range = effectiveStatus === "applied"
        ? findRangeForAppliedFinding(editor, finding, prebuilt)
        : findRangeForFinding(editor, finding, prebuilt, localOffsetsDirtyRef.current);
      if (!range) return null;
      return {
        ...range,
        isAccepted: effectiveStatus === "applied",
      };
    });

    // Abort if a newer run started while this one was computing
    if (runId !== decorationRunId.current) return;

    const ranges = results.filter(Boolean) as FactCheckDecorationRange[];
    const matched = ranges.length;
    const unmatched = activeFindings.length - matched;
    if (activeFindings.length > 0) {
      console.log(`[FactCheck] Matched ${matched}/${activeFindings.length} active findings (${unmatched} unmatched, ${factCheckFindings.length - activeFindings.length} declined)`);
    }

    (editor.commands as any).setFactCheckRanges?.(ranges);
  }, [editor, factCheckFindings]);

  const unlockArticle = () => {
    setShowPaymentModal(true);

    // Reset flags if using the regular editor
    if (!isOptimizedMode) {
      if (additionalProps.setIsDownloadContent) {
        additionalProps.setIsDownloadContent(false);
      }
      if (additionalProps.setIsCopyContent) {
        additionalProps.setIsCopyContent(false);
      }
    }
  };

  // Effect to handle unlock events from the custom TipTap extension
  useEffect(() => {
    if (!hideArticle || !useScrollBasedTruncation) {
      return;
    }

    const handleUnlockEvent = (event: CustomEvent) => {
      event.preventDefault();
      unlockArticle();
    };

    // Listen for the custom unlock event
    document.addEventListener('unlockArticle', handleUnlockEvent as EventListener);

    return () => {
      document.removeEventListener('unlockArticle', handleUnlockEvent as EventListener);
    };
  }, [hideArticle, useScrollBasedTruncation]);

  // Clean up editor on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Effect for copy/download in regular editor mode
  useEffect(() => {
    if (additionalProps.isCopyContent) {
      handleCopy();
    }
  }, [additionalProps.isCopyContent]);

  useEffect(() => {
    if (additionalProps.isDownloadContent) {
      handleDownload();
    }
  }, [additionalProps.isDownloadContent]);

  // Effect to handle content switching (only on toggle, NOT on save)
  useEffect(() => {
    if (!editor || !isContentLoaded.current) return;

    const switchContent = async () => {
      try {
        // Capture sequence number for this invocation (Fix P1)
        const mySequence = ++toggleSequence.current;

        // Flush CURRENT channel before switching (Fix #3)
        // This ensures any pending save for the current view completes first
        const currentChannel = isEditingHumanizedContent.current ? 'humanized' : 'original';

        // Block toggle on flush error to prevent data loss
        try {
          await flushPendingSaves(currentChannel);
        } catch (error) {
          console.error('[TOGGLE] Save failed during flush, aborting toggle:', error);
          // Revert toggle UI to match the channel we're still editing
          dispatch(setShowHumanizeContent(isEditingHumanizedContent.current));
          dispatch(snackAdded({
            message: "Failed to save changes. Please try toggling again.",
            type: "error",
          }));
          return;
        }

        // Check if a newer toggle was initiated while we were flushing (Fix P1)
        if (mySequence !== toggleSequence.current) {
          return; // Abort silently - newer toggle will handle the switch
        }

        // Read from refs (always latest saved content) instead of state
        const newContent = additionalProps.showHumanizeContent
          ? humanizedHtmlRef.current
          : htmlRef.current;
        const currentContent = editor.getHTML();

        if (newContent !== currentContent) {
          isProgrammaticChange.current = true;

          try {
            // Update tracking ref for new channel
            isEditingHumanizedContent.current = additionalProps.showHumanizeContent;
            setIsEditingHumanizedVersion(additionalProps.showHumanizeContent);

            // Update content
            if (hideArticle && useScrollBasedTruncation) {
              insertUnlockCardWithTipTap(editor, newContent, isOptimizedMode);
            } else {
              editor.commands.setContent(newContent);
            }
          } finally {
            // Clear suppression even if setContent throws (Fix P2)
            // Ensures flag never gets stuck, which would block all future user edits
            isProgrammaticChange.current = false;
          }

          // Update word count after content is rendered
          setTimeout(() => {
            if (editor) {
              setWordCount(getWordCount(editor.getText()));
            }
          }, 100);
        }
      } catch (error) {
        // Catch any unhandled errors in switchContent to prevent unhandled rejection (Fix P2)
        console.error('[TOGGLE] Error during content switch:', error);
      }
    };

    switchContent();
  }, [
    additionalProps.showHumanizeContent,
    editor,
    hideArticle,
    useScrollBasedTruncation,
    isOptimizedMode,
    flushPendingSaves,
  ]);

  useEffect(() => {
    const id = setTimeout(() => applyFactCheckDecorations(), 300);
    return () => clearTimeout(id);
  }, [applyFactCheckDecorations]);

  // Detect a fresh load or fresh fact-check pass by watching the set of
  // finding IDs. When the set changes, the new findings carry offsets
  // computed against current content — clear the dirty flag so Strategy 1
  // is trusted again. Apply/decline updates keep the same IDs and don't
  // reset the flag (which is the whole point of having it).
  useEffect(() => {
    const currentIds = factCheckFindings.map((f) => f.id).sort((a, b) => a - b).join(",");
    if (currentIds !== previousFindingIdsRef.current) {
      localOffsetsDirtyRef.current = false;
      previousFindingIdsRef.current = currentIds;
    }
  }, [factCheckFindings]);

  useEffect(() => {
    return () => {
      if (findingsSaveTimer.current) {
        clearTimeout(findingsSaveTimer.current);
      }
    };
  }, []);

  // Click-to-card: when user clicks on a fact-check underline in the editor,
  // set the active finding so the sidebar card scrolls into view.
  useEffect(() => {
    if (!editor) return;
    const editorEl = editor.view.dom;
    const handleFactCheckClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const decorated = target.closest("[data-fact-check-id]") as HTMLElement | null;
      if (decorated) {
        const id = Number(decorated.getAttribute("data-fact-check-id"));
        if (!isNaN(id) && id) setActiveFindingId(id);
      }
    };
    editorEl.addEventListener("click", handleFactCheckClick);
    return () => editorEl.removeEventListener("click", handleFactCheckClick);
  }, [editor]);

  // Cleanup effect: flush pending saves on unmount (Fix #5 - save-on-unmount)
  useEffect(() => {
    return () => {
      // Flush pending saves on unmount to prevent data loss
      // This is fire-and-forget since unmount cleanup can't be async
      // Catch rejection to prevent unhandled promise rejection (Fix P1)
      flushPendingSaves().catch((error) => {
        console.warn('[UNMOUNT] Failed to flush pending saves:', error);
      });
    };
  }, [flushPendingSaves]);

  // Add a new effect to track isEditingHumanizedVersion changes separately
  useEffect(() => {}, [isEditingHumanizedVersion]);

  // Add this effect to handle humanization
  useEffect(() => {
    const handleHumanization = async () => {
      // Exit early if not humanizing, no editor, or no humanize function
      if (
        !additionalProps.isHumanizing ||
        !editor ||
        !additionalProps.onHumanize
      )
        return;

      // Read-only sessions must not run humanize (it rewrites + saves content).
      if (!canEditRef.current) return;

      try {
        const content = editor.getHTML();

        // Audit stage runs server-side (~2-3s) before the rewrite stream
        // starts. Show the skeleton overlay until the first chunk arrives.
        setIsWaitingForFirstChunk(true);

        // Call the humanize-again streaming Lambda (Function URL, AWS_IAM auth).
        const response = await signedHumanizeFetch({
          text: content,
          source: "ai_writing",
        });

        if (response.status === 429) {
          const data = await response.json();
          dispatch(
            snackAdded({
              message: data.message,
              type: "error",
              duration: 2000,
            })
          );
          dispatch(endHumanization(false));
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder("utf-8");
        let accumulatedText = "";
        let firstChunk = true;

        while (true) {
          // If we were demoted to read-only mid-stream (someone took over),
          // stop applying chunks so we don't repaint / clobber the new holder.
          if (!canEditRef.current) break;

          const { value, done } = await reader?.read()!;
          if (done) break;

          if (firstChunk) {
            setIsWaitingForFirstChunk(false);
            firstChunk = false;
          }

          const chunk = decoder.decode(value, { stream: true });
          accumulatedText += chunk;

          const html = converter.makeHtml(accumulatedText);
          isProgrammaticChange.current = true;

          // Apply truncation if article should be hidden
          if (hideArticle && useScrollBasedTruncation) {
            insertUnlockCardWithTipTap(editor, html, isOptimizedMode);
          } else {
            editor.commands.setContent(html);
          }

          isProgrammaticChange.current = false;
        }

        // If we were demoted to read-only mid-stream (someone took over), do NOT
        // persist this partial rewrite over the new holder's content, and do not
        // mark the run complete — the loop already stopped applying chunks above.
        if (!canEditRef.current) {
          dispatch(endHumanization(false));
          return;
        }

        // Save content and update state
        await handleSaveContent(editor.getHTML(), {
          isHumanized: true,
          isEdited: false,
        });

        // Update states in correct order
        dispatch(endHumanization(true));

        // Fetch latest content
        await loadContent();

        // Content will be updated through the normal fetch mechanism
      } catch (error) {
        console.error("Error humanizing content:", error);
        dispatch(
          snackAdded({
            message: "Failed to humanize content",
            type: "error",
            duration: 2000,
          })
        );
      } finally {
        setIsWaitingForFirstChunk(false);
      }
    };

    handleHumanization();
  }, [additionalProps.isHumanizing, editor, additionalProps.onHumanize]);

  // Add this effect to handle navigation blocking during humanization
  useEffect(() => {
    // Block navigation while humanizing
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (additionalProps.isHumanizing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    // Block Next.js client-side navigation
    const handleRouteChange = (url: string) => {
      if (additionalProps.isHumanizing) {
        // Prevent the default behavior
        window.history.pushState(null, "", window.location.href);
        // Show warning
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
        throw "Navigation cancelled";
      }
    };

    // Handle popstate (browser back/forward buttons)
    const handlePopState = () => {
      if (additionalProps.isHumanizing) {
        // Push current URL back to history to prevent navigation
        window.history.pushState(null, "", window.location.href);
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    router.events.on("routeChangeStart", handleRouteChange);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [additionalProps.isHumanizing]);

  // Add effect to log when showHumanizeContent changes
  useEffect(() => {}, [additionalProps.showHumanizeContent]);

  const handleToggle = (event) => {
    const isChecked = event.target.checked;
    // Just update the display preference
    dispatch(setShowHumanizeContent(isChecked));
  };

  const checArticleLimit = async () => {
    // Refresh the current plan
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    const numberOfAIWords = Number(currentPlan?.numberOfWordsInPlan || 0);
    const numberOfArticles = Number(currentPlan?.numberOfArticlesInPlan || 0);
    let aiWordsLeft = numberOfAIWords - (currentPlan?.totalWordsWritten || 0);
    let articlesLeft =
      numberOfArticles - (currentPlan?.totalArticlesWritten || 0);
    if (aiWordsLeft < 0) {
      aiWordsLeft = 0;
    }
    if (articlesLeft < 0) {
      articlesLeft = 0;
    }

    if (aiWordsLeft === 0 || articlesLeft === 0) {
      let text = "";
      if (articlesLeft === 0) {
        text = `You've reached a limit for Updating Articles`;
      } else {
        text = ` You've reached a limit of word count`;
      }

      dispatch(aiWordsUpgradeModalShowed(text));
      return true;
    }
    return false;
  };
  const handleUpdateArticleEEAT = async () => {
    setIsOptimizeContentDialogLoading(true);
    setIsOptimizeContentDialogOpen(false);
    const planDetails = (await dispatch(getActivePlan())) as any;
    let plan = planDetails?.payload?.plan ?? undefined;

    if (
      plan === undefined ||
      Object.keys(plan).length === 0 ||
      planDetails?.payload?.error
    ) {
      setShowOnboardingModal(true);
      setIsOptimizeContentDialogLoading(false);
      return;
    }

    const isArticleLimitReached = await checArticleLimit();

    setIsOptimizeContentDialogLoading(false);
    if (!isArticleLimitReached) {
      setIsOptimizeContentDialogOpen(true);
    }
  };
  // On-demand E-E-A-T scoring for articles without a score (e.g. bulk
  // full-sequence runs with scoring disabled). Calls the eeat-calculator
  // Lambda, fetches the full result, persists it onto the article record,
  // and refreshes the sidebar.
  const handleGenerateEEATScore = async () => {
    if (isGeneratingEEATScore) return;
    setIsGeneratingEEATScore(true);

    try {
      const contentHtml = editor?.getHTML() || editorContent;
      const articleMarkdown = convertToMarkdown(contentHtml || "");

      if (!articleMarkdown?.trim()) {
        dispatch(
          snackAdded({
            message: "Nothing to score yet — the article has no content.",
            type: "warning",
          })
        );
        return;
      }

      const calcResult: any = await dispatch(
        calculateEEATScore({
          article_text: articleMarkdown,
          article_link: "",
          article_title: title || "",
          article_meta_description: "",
        })
      ).unwrap();

      // The calculate endpoint returns only the article scores; the full
      // result (analysis, suggestions, summary) is stored server-side and
      // fetched by the id it returns.
      let fullScore: any = calcResult?.eeat_score || null;
      const scoreId = calcResult?.article_id;
      if (scoreId) {
        try {
          const getResult: any = await dispatch(getEEATScore(scoreId)).unwrap();
          if (getResult?.data?.articleEEATScore) {
            fullScore = getResult.data.articleEEATScore;
          }
        } catch (fetchError) {
          console.error("Failed to fetch full EEAT result, falling back to scores only:", fetchError);
        }
      }

      if (!fullScore?.article) {
        throw new Error("The EEAT calculator returned no score");
      }

      // Persist onto the article record so the score survives reloads and
      // the generate button stays hidden.
      await dispatch(
        saveArticleEEATScore({ id, articleEEATScore: JSON.stringify(fullScore) })
      );

      handleSaveEvaluationData({ articleEEATScore: fullScore });

      dispatch(
        snackAdded({ message: "E-E-A-T score generated!", type: "success" })
      );
    } catch (error: any) {
      console.error("Error generating EEAT score:", error);
      dispatch(
        snackAdded({
          message: error?.message || "Failed to generate E-E-A-T score",
          type: "error",
        })
      );
    } finally {
      setIsGeneratingEEATScore(false);
    }
  };

  const handleCloseOptimizeContentDialog = () => {
    setIsOptimizeContentDialogOpen(false);
  };
  const handleOptimizeContentDialogOpen = () => {
    setIsOptimizeContentDialogOpen(true);
  };
  // "Article in use" prompt: someone else holds the lock and we've settled into
  // read-only, and the user hasn't dismissed THIS holder. Gated on `settled` so it
  // never flashes during the initial sync gap.
  const showTakeOverModal =
    presence.active &&
    presence.settled &&
    !!presence.holder &&
    !presence.isHolder &&
    presence.holder.sessionId !== dismissedHolderSessionId;

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <ReadOnlyTakeOverModal
        open={showTakeOverModal}
        holderName={presence.holder?.displayName || "Someone"}
        holderKind={presence.holder?.kind === "agent" ? "agent" : "human"}
        onViewReadOnly={() =>
          setDismissedHolderSessionId(presence.holder?.sessionId ?? null)
        }
        onTakeOver={() => {
          // Don't re-prompt for this holder while the (async, fail-closed) take-over
          // runs; if it fails we stay read-only and the next holder change re-prompts.
          setDismissedHolderSessionId(presence.holder?.sessionId ?? null);
          void presence.requestTakeOver().catch(() => {});
        }}
      />
      {pendingConflictEdit && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1,
            bgcolor: "#FEF3C7",
            borderBottom: "1px solid #F59E0B",
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#92400E" }}>
            The article was updated elsewhere, so your last change wasn’t saved. Your
            edits are preserved — restore them to re-apply on top of the current version.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: "#F59E0B", "&:hover": { bgcolor: "#D97706" } }}
              onClick={restorePendingConflictEdit}
            >
              Restore my changes
            </Button>
            <Button
              size="small"
              variant="text"
              sx={{ color: "#92400E" }}
              onClick={() => setPendingConflictEdit(null)}
            >
              Dismiss
            </Button>
          </Box>
        </Box>
      )}
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);

          // Reset flags if using the regular editor
          if (!isOptimizedMode) {
            if (additionalProps.setIsDownloadContent) {
              additionalProps.setIsDownloadContent(false);
            }
            if (additionalProps.setIsCopyContent) {
              additionalProps.setIsCopyContent(false);
            }
          }
        }}
        location={""}
      />
      <ImageGenerationModal
        open={showImageGenModal}
        onClose={() => setShowImageGenModal(false)}
        loading={imageGenLoading}
        error={imageGenError}
        onRetry={handleGenerateImages}
      />
      {showOnboardingModal && (
        <Onboarding
          onClose={() => setShowOnboardingModal(false)}
          isEEATMode={isEEATMode}
          articleLink={articleLink}
        />
      )}
      <OptimizeContentDialog
        open={isOptimizeContentDialogOpen}
        limit={false}
        onClose={handleCloseOptimizeContentDialog}
        onCreated={handleOptimizeContentDialogOpen}
        isRedirect={false}
        isShowInputs={false}
        link={articleLink}
      />
      <Box
        sx={{
          width: "100%",
          margin: "auto",
          position: "relative",
          display: "flex",
          gap: 3,
          px: 3,
          height: "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "100%",
            overflow: "auto",
            position: "relative",
          }}
        >
          <Box
            data-testid="article-editor-toolbar-shell"
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: hideArticle || isEEATMode ? 1 : 20,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: isWritingMode ? "1180px" : "1000px",
                backgroundColor: "#fff",
                borderBottom: "1px solid #EDEFF5",
                boxShadow: "0 2px 10px rgba(18, 24, 40, 0.05)",
                // Read-only sessions: disable the whole toolbar (its buttons call
                // editor.commands directly, which setEditable(false) doesn't block).
                ...(presence.canEdit
                  ? {}
                  : { pointerEvents: "none", opacity: 0.5 }),
              }}
            >
              <MenuBar
                editor={editor}
                handleAidropdown={handleAIDropdown}
                evaluationData={evaluationData}
                isEEATMode={isEEATMode}
                isWritingMode={isWritingMode}
                onToggleWritingMode={() =>
                  setIsWritingMode((prevMode) => !prevMode)
                }
                onGenerateImages={isImageGenEnabled ? handleGenerateImages : undefined}
              />
            </Box>
          </Box>
          <Card
            sx={{
              width: "100%",
              maxWidth: isWritingMode ? "1180px" : "1000px",
              mt: "12px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              flex: 1,
              ...(additionalProps.isHumanizing && {
                minHeight: "calc(100vh - 64px)",
              }),
            }}
          >
            <Editor
              editor={editor}
              isHumanizer={false}
              isLoading={isLoading}
              title={title}
              description={description}
              wordCount={wordCount}
            />

            {/* Audit-stage loader: covers the editor between Humanize click
                and first streamed chunk (~2-3s while the audit runs).
                Three motion cues so the user sees we're actively working:
                  - LinearProgress bar at the top (sliding)
                  - CircularProgress next to the caption (spinning)
                  - Skeleton lines below (subtle wave shimmer)
                Always mounted; toggled via display so React never has to
                unmount it while Tiptap is mutating the editor subtree
                (which caused a NotFoundError insertBefore crash). */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "background.paper",
                zIndex: 2,
                display: isWaitingForFirstChunk ? "flex" : "none",
                flexDirection: "column",
              }}
            >
              <LinearProgress />
              <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <CircularProgress size={16} thickness={5} />
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.75, fontStyle: "italic" }}
                  >
                    Analyzing article…
                  </Typography>
                </Box>
                <Skeleton variant="text" animation="wave" width="40%" sx={{ fontSize: "1.5rem" }} />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="92%" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="88%" />
                <Skeleton variant="text" animation="wave" width="60%" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="78%" />
              </Box>
            </Box>

            {/* Render skeletons below the Editor for hiding content - only when not using scroll-based truncation */}
            {hideArticle && !useScrollBasedTruncation && (
              <Box
                sx={{
                  width: "100%",
                  padding: "16px",
                  background: "linear-gradient(to bottom, transparent, white)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Skeleton Loaders */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={15}
                    width={"65%"}
                    animation={false}
                  />
                </Box>

                {/* Unlock Card */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "65px",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#5048E5",
                      borderRadius: "10px",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        height: "100%",
                        width: "100%",
                        justifyContent: "space-between",
                        padding: "10px 16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Your {isOptimizedMode ? "optimized " : ""}content is
                          ready
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Subscribe to see the full article
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={unlockArticle}
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                            padding: "8px 16px",
                            background: "#EB1F8D !important",
                            width: "150px",
                            height: "49px",
                            borderRadius: "8px",
                          }}
                        >
                          Unlock Now
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}

            <BubbleMenu
              editor={editor}
              className="bubble-menu"
              // Keep this mounted (conditionally unmounting a tippy-backed menu on
              // frequent presence re-renders causes React removeChild crashes).
              // Hide it when read-only via editor.isEditable (toggled by setEditable).
              shouldShow={({ editor, state }) =>
                editor.isEditable && !state.selection.empty
              }
              tippyOptions={{
                duration: 100,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "120px",
                  height: "40px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
                onClick={handleAIDropdown}
              >
                <MagicWandIcon sx={{ mt: 1 }} />
                Ask AI
              </Button>
            </BubbleMenu>

            {showAIDropdown && (
              <RegenerateDropdown
                startCord={cords.top}
                endCord={cords.left}
                bottomCord={cords.bottom}
                rightCord={cords.right}
                open={showAIDropdown}
                selectedText={selectedText}
                editor={editor}
                onClose={handleCloseAIDropdown}
                setMarginBottom={(margin) => setBottomMargin(margin)}
                scrollContainerRef={scrollContainerRef}
              />
            )}
          </Card>
        </Box>
        {/* Sidebar */}

        <Box
          sx={{
            width: "384px",
            height: "10%",
            display: isWritingMode ? "none" : "block",
            position: "relative",
            zIndex: 3,
          }}
        >
          <Sidebar
            title={
              isOptimizedMode ? "EEAT Score Comparison" : "Article EEAT Score"
            }
            recommendedScore={8.0}
            evaluationData={evaluationData}
            optimizedMode={isOptimizedMode}
            isLoading={isLoading}
            isEEATMode={isEEATMode}
            onUpdateArticleEEAT={handleUpdateArticleEEAT}
            isEEATLoading={isOptimizeContentDialogLoading}
            onGenerateEEATScore={
              !isOptimizedMode ? handleGenerateEEATScore : undefined
            }
            isGeneratingEEATScore={isGeneratingEEATScore}
            factCheckFindings={factCheckFindings}
            factCheckAutoReview={factCheckAutoReview}
            onSelectFactCheckFinding={handleSelectFactCheckFinding}

            onApplyFactCheckAction={handleApplyFactCheckAction}
            onDeclineFactCheckAction={handleDeclineFactCheckAction}
            activeFindingId={activeFindingId}
            isApplyingFactCheck={isApplyingFactCheck}
          />
        </Box>
      </Box>
    </>
  );
};

export default ArticleEditorComponent;
